(function () {
  'use strict';

  /*
    SFG Support Flow
    =================

    Declarative troubleshooting rules for sfg-support-ui.js.

    This file intentionally contains NO DOM code and NO Shopify form code.
    The UI asset renders these definitions and owns navigation/state.

    Design rule:
      Ask a question only when its answer can change the next diagnostic step,
      customer instruction, ticket status, or information SFG needs.

    Vehicle context is expected to come from:
      window.SFGVehicleDecoder

    Code analysis is expected to come from:
      window.SFGSupportCodes
  */

  var URLS = {
    hpBeta: 'https://files.hptuners.com/VCM%20Suite/VCM%20Suite%20%28Beta%29.msi',
    hpInstructions: 'https://sfgtunes.com/pages/mpvi-instructions',
    hpSupport: 'https://support.hptuners.com/support/home',
    hpCredits: 'https://www.hptuners.com/vehicles/',
    efiDownload: 'https://www.efilive.com/download-efilive',
    efiInstructions: 'https://sfgtunes.com/pages/efi-live-instructions',
    efiSupport: 'https://service.efilive.com/new-ticket',
    efiErrors: 'https://content.efilive.com/documents/EFILive%20Error%20Codes.pdf',
    ezSupport: 'https://www.ezlynk.com/support.html'
  };

  var STATUS = {
    READY: 'READY FOR SFG REVIEW',
    VEHICLE: 'LIKELY VEHICLE-SIDE',
    CALIBRATION: 'POSSIBLE CALIBRATION ISSUE',
    CONFIGURATION: 'CALIBRATION CONFIGURATION CHECK',
    RECOVERY: 'PROGRAMMING RECOVERY',
    RETRY_FLASH: 'RETRY FULL FLASH — KEEP CONTROLLER POWERED',
    REVISION: 'POSSIBLE TUNE REVISION',
    CONTROLLER: 'CONTROLLER REPLACEMENT — FILE UPDATE REQUIRED',
    GENERAL: 'GENERAL SUPPORT'
  };

  var FOLLOW = {
    HP_INFO_THEN_DATALOG: 'H',
    HP_INFO_ONLY: 'I',
    /* Backward-compatible aliases used by existing flow nodes/UI. */
    HP_LOG: 'H',
    HP_INFO: 'I',
    EFI_LOG: 'E',
    EZ_REQUEST: 'Z',
    SWITCH_LOG: 'S'
  };

  function copy(obj) {
    var out = {};
    Object.keys(obj || {}).forEach(function (key) {
      out[key] = obj[key];
    });
    return out;
  }

  function merge(base, extra) {
    var out = copy(base);
    Object.keys(extra || {}).forEach(function (key) {
      out[key] = extra[key];
    });
    return out;
  }

  function unique(list) {
    var seen = Object.create(null);
    return (list || []).filter(function (value) {
      if (!value || seen[value]) return false;
      seen[value] = true;
      return true;
    });
  }

  function targetKey(ctx) {
    return String(
      ctx && ctx.vehicle && (
        ctx.vehicle.targetKey ||
        (ctx.vehicle.target && ctx.vehicle.target.key) ||
        ctx.vehicle.engine
      ) || ''
    ).toUpperCase();
  }

  function isL5p(ctx) {
    return targetKey(ctx) === 'L5P';
  }

  function isLb7Lly(ctx) {
    var key = targetKey(ctx);
    if (key === 'LB7' || key === 'LLY') return true;

    /*
      If vPIC does not return the GM RPO text, the Duramax generation can
      still be resolved from the 8th VIN character when the decoded vehicle
      is a GM 6.6L diesel. VIN code 1 = LB7 and VIN code 2 = LLY.
    */
    var vehicle = ctx && ctx.vehicle || {};
    var vin = String(vehicle.vin || '').toUpperCase();
    var manufacturer = String(vehicle.manufacturer || '').toLowerCase();
    var fuel = String(vehicle.fuel || '').toLowerCase();
    var displacement = Number(vehicle.displacementL || 0);
    var engine = String(vehicle.engine || '').toUpperCase();
    var isGm = manufacturer === 'gm';
    var is66Diesel = (fuel.indexOf('diesel') >= 0 || /DURAMAX|LB7|LLY/.test(engine)) && Math.abs(displacement - 6.6) < 0.25;

    return !!(isGm && is66Diesel && vin.length === 17 && (vin.charAt(7) === '1' || vin.charAt(7) === '2'));
  }


  function vehicleYear(ctx) {
    var vehicle = ctx && ctx.vehicle || {};
    var direct = Number(vehicle.year || vehicle.modelYear || vehicle.ModelYear || 0);
    if (direct >= 1980 && direct <= 2100) return direct;
    var title = String(vehicle.title || '');
    var match = title.match(/\b(19|20)\d{2}\b/);
    return match ? Number(match[0]) : 0;
  }

  function isSupportedApplication(ctx) {
    var vehicle = ctx && ctx.vehicle || {};
    var key = String(
      vehicle.targetKey ||
      (vehicle.target && vehicle.target.key) ||
      ''
    ).toUpperCase();

    if (key) {
      return /^(LB7|LLY|LBZ|LMM|LML|L5P|CMB|CMC|CMD|CME|CMF|FORD-6\.0|FORD-6\.4|FORD-6\.7-2011-2014|FORD-6\.7-2015-2019|FORD-6\.7-2020-2022|FORD-6\.7-2023-2026)$/.test(key);
    }

    if (isLb7Lly(ctx)) return true;
    if (!vehicle || !Object.keys(vehicle).length) return null;

    var manufacturer = String(vehicle.manufacturer || '').toLowerCase();
    var fuel = String(vehicle.fuel || '').toLowerCase();
    var engine = String(vehicle.engine || '').toLowerCase();

    if (manufacturer && ['gm', 'ram', 'ford'].indexOf(manufacturer) < 0) return false;
    if (fuel && fuel.indexOf('diesel') < 0) return false;
    if (/gasoline|gas engine|spark ignition/.test(engine)) return false;

    /* A GM/Ram/Ford diesel with no resolved target is left unknown instead of
       being incorrectly rejected when the VIN API omitted an engine code. */
    return null;
  }

  function powerLevelControlMethod(ctx) {
    var key = targetKey(ctx);
    var year = vehicleYear(ctx);
    if (key === 'L5P') return 'cruise';
    if (key === 'CMF' && year >= 2022 && year <= 2024) return 'cruise';
    if (key) return 'physical_switch';
    return 'unknown';
  }

  function supportsStockComparison(platform) {
    return platform === 'hp' || platform === 'ez';
  }

  function evidence(type, weight, reason) {
    return { type: type, weight: weight, reason: reason || '' };
  }

  function resolveStatus(evidenceList, fallbackStatus) {
    var terminal = [STATUS.RECOVERY, STATUS.RETRY_FLASH, STATUS.CONTROLLER, STATUS.GENERAL];
    if (terminal.indexOf(fallbackStatus) >= 0) return fallbackStatus;

    var scores = { vehicle: 0, calibration: 0, configuration: 0, revision: 0 };
    (evidenceList || []).forEach(function (item) {
      if (!item || !scores.hasOwnProperty(item.type)) return;
      scores[item.type] += Number(item.weight || 0);
    });

    if (fallbackStatus === STATUS.REVISION) scores.revision = Math.max(scores.revision, 5);
    if (fallbackStatus === STATUS.CONFIGURATION) scores.configuration = Math.max(scores.configuration, 4);
    if (fallbackStatus === STATUS.CALIBRATION) scores.calibration = Math.max(scores.calibration, 3);
    if (fallbackStatus === STATUS.VEHICLE) scores.vehicle = Math.max(scores.vehicle, 3);

    if (scores.revision >= 5) return STATUS.REVISION;
    if (scores.configuration >= 4 && scores.configuration >= scores.vehicle && scores.configuration >= scores.calibration) return STATUS.CONFIGURATION;
    if (scores.calibration >= 4 && scores.calibration > scores.vehicle) return STATUS.CALIBRATION;
    if (scores.vehicle >= 4 && scores.vehicle > scores.calibration && scores.vehicle > scores.configuration) return STATUS.VEHICLE;
    return STATUS.READY;
  }

  function resultBlob(r) {
    return [r && r.code, r && r.title, r && r.category, r && r.customerText, r && r.staffText]
      .filter(Boolean).join(' ').toLowerCase();
  }

  var GRID_HEATER_CODES = {
    P0540: 1, P0541: 1, P0542: 1, P0543: 1, P2604: 1, P2605: 1, P2606: 1, P2607: 1, P2608: 1, P2609: 1
  };

  /*
    CCV faults are intentionally split. SFG only wants the tune/configuration
    branch for faults consistent with an intentionally unplugged/removed
    crankcase-pressure/CCV input (for example P04DB or P051D). Range,
    performance and operating faults remain vehicle-side CCV diagnostics.
  */
  var CCV_CONFIG_CODES = { P04DB: 1, P051D: 1 };
  var CCV_OPERATING_CODES = { P04FB: 1, P04FC: 1, P051B: 1 };

  var LIFT_PUMP_CODES = {
    P008A: 1, P008B: 1, P0230: 1, P0231: 1, P0232: 1,
    P025A: 1, P025B: 1, P025C: 1, P025D: 1,
    P0627: 1, P0628: 1, P0629: 1, P069E: 1, P2635: 1
  };

  /*
    L5P K111 Fuel Pump Driver Control Module codes. P129F is deliberately not
    in this group because SFG uses a separate lift-pump configuration path for
    that fault. The K111 module is separately coded and is not tuned by SFG.
  */
  var L5P_FPCM_CODES = {
    P1000: 1,
    P1009: 1,
    P129D: 1,
    P12A6: 1,
    P12A8: 1,
    U18A2: 1,
    U2616: 1
  };

  var L5P_FPCM_COMM_CODES = { U18A2: 1, U2616: 1 };

  /*
    Configuration-sensitive emissions/input faults. These lists intentionally
    target electrical/open/high/low/no-communication faults that can be a
    normal consequence of hardware being intentionally unplugged or removed.
    Range/performance faults remain ordinary system diagnostics unless the
    broader component group (DPF/SCR) is intentionally absent.
  */
  var THROTTLE_CONFIG_CODES = {
    P2100: 1, P2102: 1, P2103: 1
  };

  var EGR_CONFIG_CODES = {
    P0403: 1, P0405: 1, P0406: 1, P0409: 1,
    P0487: 1, P0489: 1, P0490: 1
  };

  var EXHAUST_SENSOR_CONFIG_CODES = {
    P0544: 1, P0545: 1, P0546: 1,
    P2031: 1, P2032: 1, P2033: 1,
    P2470: 1, P2471: 1,
    U029D: 1, U029E: 1, U02A3: 1
  };

  var DPF_CODES = {
    P2002: 1, P242F: 1, P244A: 1, P244B: 1,
    P2452: 1, P2453: 1, P2454: 1, P2455: 1,
    P2458: 1, P2459: 1, P2463: 1
  };

  var SCR_MODULE_CODES = {
    U010E: 1, U040F: 1, U2204: 1, U2212: 1
  };

  function isGridHeaterResult(r) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    return !!GRID_HEATER_CODES[code] || /grid heater|intake air heater/.test(blob);
  }

  function isCcvConfigResult(r) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    if (CCV_CONFIG_CODES[code]) return true;
    if (!/crankcase pressure|crankcase ventilation|\bccv\b/.test(blob)) return false;
    return /disconnected|open circuit|circuit open|signal high|circuit high/.test(blob);
  }

  function isCcvOperatingResult(r) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    if (isCcvConfigResult(r)) return false;
    if (CCV_OPERATING_CODES[code]) return true;
    return /crankcase pressure|crankcase ventilation|\bccv\b/.test(blob) &&
      /range|performance|flow|operat|plausib/.test(blob);
  }

  function isLiftPumpResult(r, ctx) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    if (code === 'P129F') return isL5p(ctx);
    return !!LIFT_PUMP_CODES[code] || /lift pump|low[- ]pressure fuel pump/.test(blob);
  }

  function isL5pFpcmResult(r, ctx) {
    if (!isL5p(ctx)) return false;
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    if (code === 'P129F') return false;
    return !!L5P_FPCM_CODES[code] || /fuel pump driver control module/.test(blob);
  }

  function isL5pFpcmCommResult(r, ctx) {
    if (!isL5p(ctx)) return false;
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    return !!L5P_FPCM_COMM_CODES[code] ||
      (/fuel pump driver control module/.test(blob) && /lost communication|communication.*lost|no communication/.test(blob));
  }

  function isThrottleConfigResult(r) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    if (THROTTLE_CONFIG_CODES[code]) return true;
    if (!/throttle actuator|throttle valve|intake throttle|throttle control/.test(blob)) return false;
    return /open circuit|circuit open|circuit high|circuit low|signal high|signal low|no signal|disconnected/.test(blob);
  }

  function isEgrConfigResult(r) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    if (EGR_CONFIG_CODES[code]) return true;
    if (!/exhaust gas recirculation|\begr\b/.test(blob)) return false;
    return /open circuit|circuit open|circuit high|circuit low|signal high|signal low|no signal|disconnected/.test(blob);
  }

  function isExhaustSensorConfigResult(r) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    if (EXHAUST_SENSOR_CONFIG_CODES[code]) return true;
    if (!/exhaust gas temperature|egt sensor|nitrogen oxides sensor|nox sensor|particulate matter sensor|exhaust pressure sensor/.test(blob)) return false;
    return /open circuit|circuit open|circuit high|circuit low|signal high|signal low|no signal|lost communication|no communication|disconnected/.test(blob);
  }

  function isDpfResult(r) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    return !!DPF_CODES[code] || /diesel particulate filter|particulate filter|exhaust filter|\bdpf\b/.test(blob);
  }

  function isScrModuleResult(r) {
    var code = String(r && r.code || '').toUpperCase();
    var blob = resultBlob(r);
    return !!SCR_MODULE_CODES[code] || /reductant control module|scr (?:control )?module|def (?:control )?module|aftertreatment control module/.test(blob);
  }

  function isVgtNoSignalResult(r) {
    if (!r || !r.vgtRelevant) return false;
    var blob = resultBlob(r);
    return !!r.circuitFault || /no signal|no feedback|feedback.*missing|open circuit|circuit open/.test(blob);
  }

  function isConfigurationAttentionResult(r, ctx) {
    return isGridHeaterResult(r) || isCcvConfigResult(r) || isLiftPumpResult(r, ctx) ||
      isThrottleConfigResult(r) || isEgrConfigResult(r) || isExhaustSensorConfigResult(r) ||
      isDpfResult(r) || isScrModuleResult(r) || isVgtNoSignalResult(r);
  }

  function resultHas(analysis, predicate) {
    return !!(
      analysis &&
      Array.isArray(analysis.results) &&
      analysis.results.some(predicate)
    );
  }

  function resultList(analysis, predicate) {
    if (!analysis || !Array.isArray(analysis.results)) return [];
    return analysis.results.filter(predicate);
  }

  /* ==========================================================
     REUSABLE CHECKS
     ========================================================== */

  var CHECKS = {
    full_flash: {
      id: 'full_flash',
      type: 'choice',
      label: 'Was the current tune installed using the complete flash method required for this tuning platform?',
      description: 'Most SFG files require a Full Flash / Write Entire unless SFG specifically instructed otherwise.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No / calibration-only flash',
          warning: {
            unresolved: 'Full Flash / Write Entire not confirmed',
            title: 'A complete flash is normally required',
            text: 'Most SFG files require a Full Flash / Write Entire unless specifically instructed otherwise. Calibration-only programming can leave conflicting calibration data. Retry using the complete flash method if applicable.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          warning: {
            unresolved: 'Full Flash / Write Entire not confirmed',
            title: 'Verify the flash method',
            text: 'Confirm the correct complete-flash method for the tuning platform before assuming the calibration itself is responsible.'
          }
        }
      ]
    },

    required_connectors: {
      id: 'required_connectors',
      type: 'choice',
      label: 'Are the required engine, airflow, boost and fuel-system connectors in the state required for this tune?',
      description: 'Required sensors must remain connected unless SFG specifically instructed otherwise. Any intentionally disconnected hardware must match the tune/setup.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Correct the connector configuration first',
            text: 'The current connector state does not match the tune/setup. Correct the connections, retry the vehicle, and return to Support if the problem remains.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Verify the connector configuration first',
            text: 'Confirm the required sensor and connector state for this tune/setup before continuing diagnosis.'
          }
        }
      ]
    },

    circuit_inspection: {
      id: 'circuit_inspection',
      type: 'choice',
      label: 'Have the connector, terminals, wiring and related component for the circuit fault been inspected?',
      description: 'Circuit high, circuit low, open-circuit and reference-voltage faults should be treated as electrical faults first.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Inspect the affected circuit first',
            text: 'Inspect the relevant connector, terminal fit, wiring and component before continuing. If several unrelated sensor circuits are faulting, also inspect shared power, ground and reference-voltage circuits.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Inspect the affected circuit first',
            text: 'Verify the affected connector, wiring and component before continuing. Do not replace several unrelated sensors independently when multiple circuit faults are present.'
          }
        }
      ]
    },

    charge_air_leaks: {
      id: 'charge_air_leaks',
      type: 'choice',
      label: 'Has the intake and charge-air system been checked for leaks, loose clamps, restrictions or disconnected plumbing?',
      description: 'This check is shown because the current fault set or symptom can be caused by airflow or boost plumbing problems.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          warning: {
            unresolved: 'Intake / charge-air leak check not performed',
            title: 'Check the intake and charge-air system',
            text: 'Loose boots, clamps, piping, intercooler connections, restrictions or other intake/charge problems can cause these faults. You may continue; SFG will see that this check is incomplete.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          warning: {
            unresolved: 'Intake / charge-air leak check not performed',
            title: 'Inspect the intake and charge-air system',
            text: 'Verify the intake and charge-air plumbing before assuming the calibration is responsible. You may continue; SFG will see that this check is incomplete.'
          }
        }
      ]
    },

    intake_configuration: {
      id: 'intake_configuration',
      type: 'choice',
      label: 'Was the intake tube or MAF housing changed from the configuration the tune was written for?',
      description: 'This is only relevant because the current fault set can be affected by intake/MAF housing design or diameter.',
      options: [
        { value: 'no', label: 'No' },
        {
          value: 'yes',
          label: 'Yes',
          meta: {
            status: STATUS.REVISION,
            note: 'The intake/MAF housing differs from the configuration the tune was written for. A paid tune revision may be required if the calibrated airflow configuration changed.'
          },
          addChecks: ['intake_sizes']
        },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    intake_sizes: {
      id: 'intake_sizes',
      type: 'text',
      label: 'What was the previous intake/MAF housing size and what is installed now?',
      placeholder: 'Example: 4-inch before, 5-inch now',
      description: 'Include diameter and any relevant intake/MAF housing change.'
    },

    vgt_response: {
      id: 'vgt_response',
      type: 'choice',
      label: 'If this application provides turbo/vane position feedback, what best describes the response?',
      description: 'Only use feedback that the vehicle actually reports. Some applications do not provide a usable vane-position PID.',
      options: [
        {
          value: 'no_feedback',
          label: 'No usable position feedback on a system that should report it',
          meta: {
            status: STATUS.VEHICLE,
            note: 'Expected turbo/vane position feedback is missing. Inspect turbo/actuator wiring, the actuator and turbo hardware.'
          }
        },
        {
          value: 'not_following',
          label: 'Actual position does not follow commanded position',
          meta: {
            status: STATUS.VEHICLE,
            note: 'Turbo/vane feedback is present but does not follow commanded position. Turbo actuator, vane movement and related hardware should be investigated.'
          }
        },
        {
          value: 'follows_boost_wrong',
          label: 'Position follows commanded, but boost still misses target',
          addChecks: ['charge_air_leaks']
        },
        { value: 'not_monitored', label: 'This setup does not monitor vane/turbo position' },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    fuel_hardware_change: {
      id: 'fuel_hardware_change',
      type: 'choice',
      label: 'Was the fuel system changed from the configuration the tune was written for?',
      description: 'Same-spec replacement components are different from a performance or different-spec fuel-system change.',
      options: [
        { value: 'no', label: 'No fuel-system change' },
        { value: 'same_spec', label: 'Only same-spec replacement components' },
        {
          value: 'different_spec',
          label: 'Performance / different-spec fuel-system change',
          meta: {
            status: STATUS.REVISION,
            note: 'The fuel-system configuration differs from the configuration originally tuned. A paid tune revision may be required.'
          }
        },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    exhaust_configuration: {
      id: 'exhaust_configuration',
      type: 'choice',
      label: 'Was the exhaust or aftertreatment hardware changed from the configuration the tune was written for?',
      options: [
        { value: 'no', label: 'No' },
        {
          value: 'yes',
          label: 'Yes',
          meta: {
            status: STATUS.REVISION,
            note: 'Exhaust/aftertreatment hardware changed. SFG should review whether updated tuning is required.'
          },
          addChecks: ['hardware_description']
        },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    hardware_description: {
      id: 'hardware_description',
      type: 'text',
      label: 'Describe the relevant hardware changes.',
      placeholder: 'List the parts or configuration changes that differ from when the tune was written.'
    },

    stock_compatibility: {
      id: 'stock_compatibility',
      type: 'choice',
      label: 'Is the current hardware compatible with the stock calibration?',
      description: 'Do not flash stock if the installed hardware requires modified calibration.',
      options: [
        { value: 'yes', label: 'Yes', addChecks: ['stock_comparison'] },
        {
          value: 'no',
          label: 'No',
          meta: {
            note: 'Stock calibration test not performed because the installed hardware is not compatible with the stock calibration.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          meta: {
            note: 'Stock-calibration compatibility is uncertain. SFG should review the setup before a stock-calibration comparison is attempted.'
          }
        }
      ]
    },

    stock_comparison: {
      id: 'stock_comparison',
      type: 'choice',
      label: 'After the basic vehicle-side checks, does the concern remain on the stock calibration?',
      options: [
        {
          value: 'remains',
          label: 'Yes — the problem remains on stock',
          meta: {
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 6, reason: 'The same concern remains on a compatible stock calibration.' },
            note: 'The concern remains on the stock calibration, which strongly points toward a vehicle-side issue rather than a calibration issue.'
          }
        },
        {
          value: 'gone',
          label: 'No — the problem is gone on stock',
          meta: {
            status: STATUS.CALIBRATION,
            evidence: { type: 'calibration', weight: 6, reason: 'The concern disappears on a compatible stock calibration.' },
            note: 'The concern does not occur on the stock calibration. This is strong evidence for SFG calibration review/correction.'
          }
        },
        {
          value: 'not_tested',
          label: 'Not tested yet',
          stop: {
            title: 'Perform the stock comparison first',
            text: 'Because the hardware is stock-compatible, compare the concern on the stock calibration and return with the result.'
          }
        }
      ]
    },

    weak_crank: {
      id: 'weak_crank',
      type: 'choice',
      label: 'Is cranking weak, slow, or accompanied by flickering/resetting electronics?',
      options: [
        { value: 'no', label: 'No — cranking/electrical power appears normal' },
        {
          value: 'yes',
          label: 'Yes',
          warning: {
            unresolved: 'Weak cranking / unstable vehicle power',
            title: 'Stabilize vehicle power first',
            text: 'Use a suitable charger or power source and retry. Weak cranking or electronics resetting can cause a no-start or communication problem.'
          }
        },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    security_history: {
      id: 'security_history',
      type: 'choice',
      label: 'Has the ECM, BCM, keys, ignition/security system or another security-related module been replaced or reprogrammed?',
      options: [
        { value: 'no', label: 'No' },
        { value: 'complete', label: 'Yes — programming is confirmed complete' },
        {
          value: 'uncertain',
          label: 'Yes / not sure programming is complete',
          meta: {
            status: STATUS.VEHICLE,
            note: 'Security/immobilizer programming is not confirmed. Vehicle-side programming should be verified before a tune revision is assumed.'
          }
        }
      ]
    },

    transmission_built: {
      id: 'transmission_built',
      type: 'choice',
      label: 'Is the transmission built or modified internally?',
      options: [
        { value: 'no', label: 'No' },
        { value: 'yes', label: 'Yes', addChecks: ['transmission_builder_info'] },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    transmission_builder_info: {
      id: 'transmission_builder_info',
      type: 'choice',
      label: 'Has SFG already received the transmission builder build sheet or requested calibration parameters?',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          warning: {
            unresolved: 'Transmission builder information not provided/confirmed',
            title: 'Get the builder information',
            text: 'Obtain the builder build sheet or requested calibration parameters and send them to SFG with the order number.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          warning: {
            unresolved: 'Transmission builder information not provided/confirmed',
            title: 'Confirm the builder information',
            text: 'Confirm whether SFG has the builder-requested calibration information. You may continue; this will be flagged.'
          }
        }
      ]
    },

    shared_electrical: {
      id: 'shared_electrical',
      type: 'choice',
      label: 'Have shared sensor power, ground and reference-voltage circuits been checked before treating these as separate sensor failures?',
      description: 'Multiple unrelated circuit faults can share a 5 V reference, power feed, ground or harness problem. Check the shared circuit first.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No / Not sure',
          stop: {
            title: 'Check the shared electrical circuit first',
            text: 'When several unrelated sensor circuits fault together, inspect shared power, ground, reference-voltage and harness paths before diagnosing each sensor independently.'
          }
        }
      ]
    },

    concern_timing: {
      id: 'concern_timing',
      type: 'choice',
      label: 'Did the vehicle operate correctly in the same hardware configuration before the current tune/file was installed?',
      description: 'Timing alone does not prove a calibration problem, but it is useful evidence when no stronger fault has already identified the cause.',
      options: [
        {
          value: 'yes',
          label: 'Yes — same hardware worked correctly before this tune/file',
          meta: {
            evidence: { type: 'calibration', weight: 2, reason: 'Same hardware configuration operated correctly before the current tune/file.' }
          }
        },
        { value: 'no', label: 'No — the concern existed before this tune/file' },
        { value: 'hardware_changed', label: 'Hardware changed at the same time', meta: { evidence: { type: 'configuration', weight: 1, reason: 'Hardware changed at the same time as the calibration, so the cause is not isolated.' } } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    grid_heater_configuration: {
      id: 'grid_heater_configuration',
      type: 'choice',
      label: 'Is the grid heater / intake-air-heater hardware intentionally removed, disabled or otherwise changed from stock?',
      description: 'This code is being treated as a calibration-configuration attention item because grid-heater functions are commonly requested to be disabled in tuning.',
      options: [
        {
          value: 'no',
          label: 'No — it is present and expected to operate normally',
          meta: {
            gridHeaterAltered: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 4, reason: 'Grid-heater-related fault is present while the grid-heater system is expected to be installed and operating normally.' }
          }
        },
        { value: 'yes', label: 'Yes — intentionally removed/disabled/changed', addChecks: ['grid_heater_ordered'], meta: { gridHeaterAltered: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    grid_heater_ordered: {
      id: 'grid_heater_ordered',
      type: 'choice',
      label: 'Was SFG told that the grid heater / intake-air-heater function needed to be accounted for or disabled in the tune?',
      options: [
        {
          value: 'yes',
          label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — grid-heater-related DTC is present and the customer says SFG was asked to account for the intentionally changed/disabled grid-heater setup.',
            evidence: { type: 'configuration', weight: 5, reason: 'Grid-heater hardware/function was intentionally changed and was reported as part of the requested calibration configuration.' },
            note: 'A grid-heater-related fault is present even though the customer reports that this hardware/function was intentionally changed and was included in the requested tune configuration. SFG should verify that the calibration matches the ordered configuration.'
          }
        },
        {
          value: 'no',
          label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'Grid-heater hardware/function differs from the configuration originally supplied to SFG.' },
            note: 'The grid-heater configuration differs from what SFG was originally told. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — grid-heater-related DTC with intentionally changed/disabled hardware; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 3, reason: 'Grid-heater setup is intentionally changed but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },

    ccv_configuration: {
      id: 'ccv_configuration',
      type: 'choice',
      label: 'Is the CCV / crankcase-pressure sensor intentionally unplugged, removed or otherwise intentionally disabled?',
      description: 'This question is only shown for a disconnected/open/high-signal type CCV fault. CCV operating/range/performance faults stay on the vehicle-side CCV diagnostic path.',
      options: [
        {
          value: 'no',
          label: 'No — the sensor/system is present and expected to operate normally',
          meta: {
            ccvAltered: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 4, reason: 'CCV disconnected/open/high-signal fault is present while the CCV pressure sensor/system is expected to be connected and operating normally.' }
          }
        },
        { value: 'yes', label: 'Yes — intentionally removed/disabled/changed', addChecks: ['ccv_ordered'], meta: { ccvAltered: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    ccv_ordered: {
      id: 'ccv_ordered',
      type: 'choice',
      label: 'Was SFG told that the CCV/crankcase-pressure setup needed to be accounted for or disabled in the tune?',
      options: [
        {
          value: 'yes',
          label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — CCV/crankcase-pressure DTC is present and the customer says SFG was asked to account for the intentionally changed/disabled CCV setup.',
            evidence: { type: 'configuration', weight: 5, reason: 'CCV/crankcase-pressure hardware/function was intentionally changed and was reported as part of the requested calibration configuration.' },
            note: 'A CCV/crankcase-pressure fault is present even though the customer reports that this setup was intentionally changed and included in the requested tune configuration. SFG should verify the calibration configuration.'
          }
        },
        {
          value: 'no',
          label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'CCV/crankcase-pressure setup differs from the configuration originally supplied to SFG.' },
            note: 'The CCV/crankcase-pressure configuration differs from what SFG was originally told. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — CCV/crankcase-pressure DTC with intentionally changed/disabled hardware; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 3, reason: 'CCV/crankcase-pressure setup is intentionally changed but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },


    throttle_configuration: {
      id: 'throttle_configuration',
      type: 'choice',
      label: 'Is the intake throttle / throttle valve intentionally unplugged, removed or disabled?',
      description: 'This question is shown for throttle electrical/open/high/low faults that can occur when the throttle is intentionally disconnected. Throttle operating/performance faults are not treated this way.',
      options: [
        {
          value: 'no',
          label: 'No — it is connected and expected to operate normally',
          meta: {
            throttleAltered: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 4, reason: 'Throttle electrical fault is present while the throttle is expected to be connected and operating normally.' }
          }
        },
        { value: 'yes', label: 'Yes — intentionally unplugged/removed/disabled', addChecks: ['throttle_ordered'], meta: { throttleAltered: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    throttle_ordered: {
      id: 'throttle_ordered',
      type: 'choice',
      label: 'Was SFG told that the throttle would be intentionally unplugged, removed or disabled when the tune was ordered?',
      options: [
        {
          value: 'yes', label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — throttle is intentionally unplugged/removed/disabled and a related electrical DTC remains active.',
            evidence: { type: 'configuration', weight: 5, reason: 'Throttle was intentionally disconnected/removed and that configuration was supplied to SFG.' },
            note: 'A throttle electrical fault remains active even though the customer says the throttle was intentionally disconnected/removed and SFG was told. SFG should review the calibration configuration.'
          }
        },
        {
          value: 'no', label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'Throttle configuration differs from what was originally supplied to SFG.' },
            note: 'The throttle configuration differs from what SFG was originally told. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure', label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — throttle is intentionally disconnected/removed; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 3, reason: 'Throttle is intentionally disconnected/removed but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },

    egr_configuration: {
      id: 'egr_configuration',
      type: 'choice',
      label: 'Is the EGR system intentionally unplugged, removed or disabled?',
      description: 'This question is shown for EGR electrical/open/high/low/no-signal faults. EGR flow, response and performance faults remain normal EGR-system diagnostics.',
      options: [
        {
          value: 'no', label: 'No — it is connected and expected to operate normally',
          meta: {
            egrAltered: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 4, reason: 'EGR electrical fault is present while the EGR system is expected to be connected and operating normally.' }
          }
        },
        { value: 'yes', label: 'Yes — intentionally unplugged/removed/disabled', addChecks: ['egr_ordered'], meta: { egrAltered: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    egr_ordered: {
      id: 'egr_ordered',
      type: 'choice',
      label: 'Was SFG told that the EGR system would be intentionally unplugged, removed or disabled when the tune was ordered?',
      options: [
        {
          value: 'yes', label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — EGR is intentionally unplugged/removed/disabled and a related electrical DTC remains active.',
            evidence: { type: 'configuration', weight: 5, reason: 'EGR was intentionally disconnected/removed and that configuration was supplied to SFG.' },
            note: 'An EGR electrical fault remains active even though the customer says the EGR was intentionally disconnected/removed and SFG was told. SFG should review the calibration configuration.'
          }
        },
        {
          value: 'no', label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'EGR configuration differs from what was originally supplied to SFG.' },
            note: 'The EGR configuration differs from what SFG was originally told. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure', label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — EGR is intentionally disconnected/removed; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 3, reason: 'EGR is intentionally disconnected/removed but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },

    exhaust_sensor_configuration: {
      id: 'exhaust_sensor_configuration',
      type: 'choice',
      label: 'Are the affected exhaust / aftertreatment sensor(s) intentionally unplugged or removed?',
      description: 'This question is shown for electrical/no-signal/lost-communication faults from exhaust sensors such as EGT, NOx or particulate-matter sensors. Range/performance faults remain sensor/system diagnostics.',
      options: [
        {
          value: 'no', label: 'No — the affected sensor(s) are installed and expected to operate normally',
          meta: {
            exhaustSensorAltered: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 4, reason: 'Exhaust/aftertreatment sensor electrical or communication fault is present while the sensor is expected to be installed and operating normally.' }
          }
        },
        { value: 'yes', label: 'Yes — intentionally unplugged/removed', addChecks: ['exhaust_sensor_ordered'], meta: { exhaustSensorAltered: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    exhaust_sensor_ordered: {
      id: 'exhaust_sensor_ordered',
      type: 'choice',
      label: 'Was SFG told that these exhaust / aftertreatment sensors would be unplugged or removed when the tune was ordered?',
      options: [
        {
          value: 'yes', label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — exhaust/aftertreatment sensor is intentionally unplugged/removed and a related DTC remains active.',
            evidence: { type: 'configuration', weight: 5, reason: 'Exhaust/aftertreatment sensor was intentionally removed/disconnected and that configuration was supplied to SFG.' },
            note: 'A related exhaust-sensor fault remains active even though the customer says the sensor was intentionally removed/disconnected and SFG was told. SFG should review the calibration configuration.'
          }
        },
        {
          value: 'no', label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'Exhaust-sensor configuration differs from what was originally supplied to SFG.' },
            note: 'The exhaust/aftertreatment sensor configuration differs from what SFG was originally told. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure', label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — exhaust/aftertreatment sensor is intentionally absent; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 3, reason: 'Exhaust/aftertreatment sensor is intentionally absent but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },

    dpf_configuration: {
      id: 'dpf_configuration',
      type: 'choice',
      label: 'Is the diesel particulate filter / exhaust filter system intentionally removed or disabled?',
      description: 'DPF/exhaust-filter codes can be normal vehicle-side aftertreatment faults when the system is present, or a calibration/configuration concern when the system was intentionally removed and the tune was supposed to account for it.',
      options: [
        {
          value: 'no', label: 'No — the filter system is installed and expected to operate normally',
          meta: {
            dpfAltered: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 4, reason: 'DPF/exhaust-filter fault is present while the filter system is installed and expected to operate normally.' }
          }
        },
        { value: 'yes', label: 'Yes — intentionally removed/disabled', addChecks: ['dpf_ordered'], meta: { dpfAltered: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    dpf_ordered: {
      id: 'dpf_ordered',
      type: 'choice',
      label: 'Was SFG told that the DPF / exhaust filter system would be removed or disabled when the tune was ordered?',
      options: [
        {
          value: 'yes', label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — DPF/exhaust-filter system is intentionally removed/disabled and a related DTC remains active.',
            evidence: { type: 'configuration', weight: 5, reason: 'DPF/exhaust-filter system was intentionally removed/disabled and that configuration was supplied to SFG.' },
            note: 'A DPF/exhaust-filter fault remains active even though the customer says the system was intentionally removed/disabled and SFG was told. SFG should review the calibration configuration.'
          }
        },
        {
          value: 'no', label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'DPF/exhaust-filter configuration differs from what was originally supplied to SFG.' },
            note: 'The DPF/exhaust-filter configuration differs from what SFG was originally told. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure', label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — DPF/exhaust-filter system is intentionally absent; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 3, reason: 'DPF/exhaust-filter system is intentionally absent but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },

    scr_module_configuration: {
      id: 'scr_module_configuration',
      type: 'choice',
      label: 'Is the SCR / DEF / reductant control module intentionally removed or disabled as part of the configured emissions setup?',
      description: 'A required SCR/reductant module with a genuine communication fault is a vehicle-side module/network problem. This question checks only whether the module is intentionally absent as part of the tuned configuration.',
      options: [
        {
          value: 'no', label: 'No — the module is installed and expected to communicate normally',
          meta: {
            scrModuleAltered: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 4, reason: 'SCR/reductant control module fault is present while the module is expected to be installed and communicating normally.' }
          }
        },
        { value: 'yes', label: 'Yes — intentionally removed/disabled', addChecks: ['scr_module_ordered'], meta: { scrModuleAltered: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    scr_module_ordered: {
      id: 'scr_module_ordered',
      type: 'choice',
      label: 'Was SFG told that the SCR / DEF / reductant control module would be intentionally removed or disabled when the tune was ordered?',
      options: [
        {
          value: 'yes', label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — SCR/reductant control module is intentionally absent/disabled and a related DTC remains active.',
            evidence: { type: 'configuration', weight: 5, reason: 'SCR/reductant control module was intentionally removed/disabled and that configuration was supplied to SFG.' },
            note: 'A related SCR/reductant-module fault remains active even though the customer says the module was intentionally removed/disabled and SFG was told. SFG should review the calibration configuration.'
          }
        },
        {
          value: 'no', label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'SCR/reductant module configuration differs from what was originally supplied to SFG.' },
            note: 'The SCR/reductant module configuration differs from what SFG was originally told. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure', label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — SCR/reductant module is intentionally absent/disabled; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 3, reason: 'SCR/reductant module is intentionally absent/disabled but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },

    lift_pump_configuration: {
      id: 'lift_pump_configuration',
      type: 'choice',
      label: 'Is the lift-pump / low-pressure fuel-supply setup intentionally changed from the stock configuration?',
      description: 'Lift-pump-related faults are checked against the requested tune configuration because these systems are often changed or intentionally handled differently in tuning.',
      options: [
        {
          value: 'no',
          label: 'No — stock-style setup is expected to operate normally',
          meta: {
            liftPumpAltered: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 5, reason: 'Lift-pump/low-pressure-fuel fault is present while the factory/stock-style lift-pump setup is expected to be connected and operating normally.' }
          }
        },
        { value: 'yes', label: 'Yes — intentionally changed/disabled/rewired', addChecks: ['lift_pump_ordered'], meta: { liftPumpAltered: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    lift_pump_ordered: {
      id: 'lift_pump_ordered',
      type: 'choice',
      label: 'Was SFG told about this lift-pump / low-pressure fuel-supply configuration when the tune was ordered?',
      options: [
        {
          value: 'yes',
          label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — lift-pump/low-pressure-fuel DTC is present and the customer says the non-stock lift-pump setup was supplied to SFG with the tune configuration.',
            evidence: { type: 'configuration', weight: 5, reason: 'Lift-pump/low-pressure-fuel setup was intentionally changed and was reported as part of the requested calibration configuration.' },
            note: 'A lift-pump/low-pressure-fuel fault is present even though the customer reports that the non-stock setup was included in the requested tune configuration. SFG should verify the calibration/configuration.'
          }
        },
        {
          value: 'no',
          label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'Lift-pump/low-pressure-fuel setup differs from the configuration originally supplied to SFG.' },
            note: 'The lift-pump/low-pressure-fuel configuration differs from what SFG was originally told. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — lift-pump/low-pressure-fuel DTC with a changed setup; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 3, reason: 'Lift-pump/low-pressure-fuel setup is changed but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },

    l5p_fpcm_fuel_gauge: {
      id: 'l5p_fpcm_fuel_gauge',
      type: 'choice',
      label: 'Is the fuel gauge also not reading correctly?',
      description: 'On an L5P, a Fuel Pump Driver Control Module fault together with a fuel-gauge problem strongly points toward a CAN line break rather than a tune issue.',
      options: [
        {
          value: 'yes',
          label: 'Yes — the fuel gauge is also not reading correctly',
          addChecks: ['l5p_fpcm_can_plugs'],
          meta: {
            fpcmGaugeIssue: true,
            evidence: { type: 'vehicle', weight: 6, reason: 'L5P Fuel Pump Driver Control Module fault is accompanied by a fuel-gauge communication symptom, strongly pointing toward a CAN-line/connection problem.' },
            flag: 'L5P CAN-LINE ATTENTION — Fuel Pump Driver Control Module fault plus fuel gauge not reading. Check CAN plugs at the NOx sensors and main DEF tank connection.',
            note: 'The combination of a Fuel Pump Driver Control Module fault and an inoperative/incorrect fuel gauge strongly suggests a CAN line break or connection problem on the L5P.'
          }
        },
        {
          value: 'no',
          label: 'No — the fuel gauge reads normally',
          meta: {
            fpcmGaugeIssue: false,
            status: STATUS.VEHICLE,
            evidence: { type: 'vehicle', weight: 6, reason: 'Isolated L5P Fuel Pump Driver Control Module fault with normal fuel gauge; the separately coded driver module is not tuned by SFG.' },
            flag: 'LIKELY VEHICLE-SIDE — isolated L5P Fuel Pump Driver Control Module fault; SFG does not tune the separately coded K111 module.',
            note: 'With the fuel gauge operating normally and no broader CAN symptom identified, an isolated Fuel Pump Driver Control Module fault is more likely a vehicle/module-side issue. SFG does not tune that separately coded module.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          warning: {
            unresolved: 'Fuel gauge operation not confirmed with L5P Fuel Pump Driver Control Module fault',
            title: 'Check the fuel gauge before deciding the diagnostic direction',
            text: 'Confirm whether the fuel gauge is reading normally. A Fuel Pump Driver Control Module fault together with a fuel-gauge problem points toward a CAN line break and changes the next diagnostic step.'
          }
        }
      ]
    },

    l5p_fpcm_can_plugs: {
      id: 'l5p_fpcm_can_plugs',
      type: 'choice',
      label: 'Have the L5P CAN plug connections been checked?',
      description: 'Check the CAN plug connections at the NOx sensors and the main DEF tank connection. A poor/open connection here can break the CAN line and produce the Fuel Pump Driver Control Module / fuel-gauge symptom combination.',
      options: [
        {
          value: 'yes',
          label: 'Yes — those CAN connections have been checked',
          meta: {
            evidence: { type: 'vehicle', weight: 2, reason: 'Customer reports the L5P NOx-sensor and main DEF-tank CAN plug connections have been checked.' }
          }
        },
        {
          value: 'no',
          label: 'No / Not yet',
          warning: {
            unresolved: 'L5P CAN plugs at NOx sensors / main DEF tank connection not checked',
            title: 'Check the L5P CAN plug connections',
            text: 'Inspect and fully seat the CAN plug connections at the NOx sensors and the main DEF tank connection, then recheck the Fuel Pump Driver Control Module communication and fuel-gauge operation.'
          }
        }
      ]
    },

    vgt_configuration: {
      id: 'vgt_configuration',
      type: 'choice',
      label: 'Is this truck intentionally configured with a non-VGT turbo?',
      description: 'A missing VGT signal on a truck that is intentionally non-VGT can indicate that the calibration is still expecting VGT feedback.',
      options: [
        { value: 'no', label: 'No — it should have a functioning VGT system', meta: { nonVgt: false } },
        { value: 'yes', label: 'Yes — intentionally non-VGT', addChecks: ['vgt_nonvgt_ordered'], meta: { nonVgt: true } },
        { value: 'unsure', label: 'Not sure' }
      ]
    },

    vgt_nonvgt_ordered: {
      id: 'vgt_nonvgt_ordered',
      type: 'choice',
      label: 'Was SFG told that this truck is non-VGT when the current tune was ordered?',
      options: [
        {
          value: 'yes',
          label: 'Yes',
          meta: {
            status: STATUS.CONFIGURATION,
            flag: 'CALIBRATION CONFIGURATION CHECK — truck is intentionally non-VGT but a VGT signal/feedback fault is present; customer says the non-VGT setup was supplied to SFG.',
            evidence: { type: 'configuration', weight: 6, reason: 'Truck is intentionally non-VGT, SFG was told, and the calibration is still producing a VGT signal/feedback fault.' },
            note: 'This truck is intentionally non-VGT and SFG was told that when the tune was ordered, but a VGT signal/feedback fault is present. SFG should verify that the calibration is not still expecting VGT feedback.'
          }
        },
        {
          value: 'no',
          label: 'No',
          meta: {
            status: STATUS.REVISION,
            evidence: { type: 'revision', weight: 5, reason: 'Truck is now non-VGT but the tune was not ordered for the non-VGT configuration.' },
            note: 'The truck is non-VGT but that configuration was not supplied when the tune was ordered. A paid tune revision may be required.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          meta: {
            flag: 'CALIBRATION CONFIGURATION CHECK — truck is intentionally non-VGT and has a VGT signal/feedback fault; customer is unsure whether SFG was told.',
            evidence: { type: 'configuration', weight: 4, reason: 'Truck is intentionally non-VGT but the ordered calibration configuration is uncertain.' }
          }
        }
      ]
    },

    hp_remote_correct_mpvi: {
      id: 'hp_remote_correct_mpvi',
      type: 'choice',
      label: 'Is the correct MPVI connected for this Remote Tuning file?',
      description: 'Remote Tuning files must be opened with the MPVI/interface associated with the applicable Remote Tuning network and tune file.',
      options: [
        { value: 'yes', label: 'Yes — the correct MPVI is connected' },
        {
          value: 'no',
          label: 'No / A different MPVI is connected',
          stop: {
            title: 'Connect the correct MPVI first',
            text: 'Connect the MPVI associated with this Remote Tuning file/order, then reopen the file. A different MPVI can produce the Remote Tuning network error.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Confirm the correct MPVI first',
            text: 'Confirm that the MPVI connected to the computer is the one associated with this Remote Tuning file/order, then retry. If you cannot determine which MPVI is correct, create an SFG Support ticket.'
          }
        }
      ]
    },

    hp_remote_fresh_download: {
      id: 'hp_remote_fresh_download',
      type: 'choice',
      label: 'Have you downloaded a fresh copy of the tune file from the original email and tried opening that new copy?',
      description: 'A tune file can occasionally be corrupted during download or while being saved from email.',
      options: [
        {
          value: 'yes',
          label: 'Yes — a fresh download gives the same error',
          meta: {
            flag: 'HP REMOTE TUNING FILE ERROR — correct MPVI confirmed, current VCM Suite BETA confirmed, and a fresh file download still gives the Remote Tuning network error.',
            note: 'The Remote Tuning file still reports that the connected setup is not part of the required network after the correct MPVI, current VCM Suite BETA and a fresh file download were confirmed. Create an SFG Support ticket so SFG can resolve the file/network association.'
          }
        },
        {
          value: 'no',
          label: 'No — I have not downloaded it again',
          stop: {
            title: 'Download a fresh copy of the tune file',
            text: 'Download the tune file again from the original email and open the newly downloaded copy. If the same Remote Tuning error remains, return to Support and continue this flow.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure which copy I am using',
          stop: {
            title: 'Download a fresh copy of the tune file',
            text: 'Download the tune file again from the original email so you know you are testing a fresh copy, then retry.'
          }
        }
      ]
    },

    hp_beta: {
      id: 'hp_beta',
      type: 'choice',
      label: 'Are you using the current VCM Suite BETA?',
      description: 'Use the BETA specifically, not Stable.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Install the Latest VCM Suite BETA',
            text: 'Install and launch the current VCM Suite BETA, then retry.',
            url: URLS.hpBeta,
            button: 'Download VCM Suite BETA'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Verify the VCM Suite BETA first',
            text: 'If unsure which version is installed, install the current BETA and retry from that version.',
            url: URLS.hpBeta,
            button: 'Download VCM Suite BETA'
          }
        }
      ]
    },

    hp_firmware: {
      id: 'hp_firmware',
      type: 'choice',
      label: 'Is the HP Tuners interface firmware current?',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Update the interface firmware first',
            text: 'Update/resync the HP Tuners interface using VCM Suite BETA, then retry.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Verify interface firmware first',
            text: 'Connect the interface to VCM Suite BETA and verify/update its firmware before retrying.'
          }
        }
      ]
    },

    hp_internet: {
      id: 'hp_internet',
      type: 'choice',
      label: 'Does the computer have stable internet access?',
      description: 'HP Tuners requires stable internet for reading/programming.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Use stable internet first',
            text: 'Move to a stable internet connection and retry.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Verify internet connectivity first',
            text: 'Confirm stable internet access before retrying the HP Tuners operation.'
          }
        }
      ]
    },

    battery_support: {
      id: 'battery_support',
      type: 'choice',
      label: 'Is vehicle battery voltage being maintained with a suitable charger or programming power supply?',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          warning: {
            unresolved: 'Battery support not used during programming',
            title: 'Battery support is strongly recommended',
            text: 'Programming without stable battery support can cause write failures. You may continue, but SFG recommends retrying with suitable battery support.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          warning: {
            unresolved: 'Battery support not confirmed',
            title: 'Verify battery support',
            text: 'Unstable vehicle voltage can cause programming failures. You may continue; this will be flagged.'
          }
        }
      ]
    },

    network_interference: {
      id: 'network_interference',
      type: 'choice',
      label: 'Have likely vehicle-network interference sources been isolated?',
      description: 'Disconnect aftermarket OBD/CAN equipment first. Some known OEM modules can interfere on specific vehicles; do not randomly pull fuses.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No / Not sure',
          warning: {
            unresolved: 'Network-interference check not completed',
            title: 'Check for CAN/network interference',
            text: 'Disconnect aftermarket OBD/CAN devices and isolate only known relevant OEM-module interference. Do not randomly pull fuses. You may continue; SFG will see this check is incomplete.'
          }
        }
      ]
    },

    efi_bbx: {
      id: 'efi_bbx',
      type: 'choice',
      label: 'Are the correct BBX controller settings programmed into the AutoCal/FlashScan?',
      description: 'This check is shown because the current operation/error can depend on BBX controller configuration.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Correct the BBX configuration first',
            text: 'Review the BBX section of the SFG EFILive instructions, program the correct controller settings, and retry.',
            url: URLS.efiInstructions,
            button: 'Open EFILive Instructions'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          warning: {
            unresolved: 'EFILive BBX configuration not confirmed',
            title: 'Verify the BBX controller settings',
            text: 'Review the BBX section of the SFG EFILive instructions. You may continue; unresolved BBX configuration will be included with the ticket.',
            url: URLS.efiInstructions,
            button: 'Open EFILive Instructions'
          }
        }
      ]
    },

    efi_firmware: {
      id: 'efi_firmware',
      type: 'choice',
      label: 'Is the AutoCal/FlashScan firmware current?',
      description: 'This question is only shown when the reported EFILive error makes firmware/version compatibility relevant.',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Update the EFILive device first',
            text: 'Update the AutoCal/FlashScan firmware and retry the operation.',
            url: URLS.efiDownload,
            button: 'Open EFILive Downloads'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Verify device firmware first',
            text: 'Verify/update the AutoCal/FlashScan firmware before retrying.',
            url: URLS.efiDownload,
            button: 'Open EFILive Downloads'
          }
        }
      ]
    },

    ignition_on: {
      id: 'ignition_on',
      type: 'choice',
      label: 'Is the vehicle ignition fully ON rather than in Accessory mode?',
      options: [
        { value: 'yes', label: 'Yes — ignition is ON' },
        {
          value: 'no',
          label: 'No / Not sure',
          stop: {
            title: 'Use full Ignition ON first',
            text: 'Put the vehicle in full Ignition ON rather than Accessory mode and retry communication.'
          }
        }
      ]
    },

    obd_power_network: {
      id: 'obd_power_network',
      type: 'choice',
      label: 'Have OBD power, vehicle communication and likely network-interference sources been checked?',
      options: [
        { value: 'yes', label: 'Yes' },
        {
          value: 'no',
          label: 'No / Not sure',
          warning: {
            unresolved: 'Vehicle OBD/network checks incomplete',
            title: 'Check vehicle communication basics',
            text: 'Verify OBD power and vehicle communication, disconnect unnecessary aftermarket OBD/CAN equipment, and retry.'
          }
        }
      ]
    }
  };

  /* ==========================================================
     DTC-DRIVEN QUESTION PLAN
     ========================================================== */

  function deriveDtcPlan(analysis, ctx) {
    var checks = [];
    var notes = [];
    var flags = [];
    var evidenceList = [];
    var status = STATUS.READY;
    var follow = '';

    var results = analysis && analysis.results || [];
    var limitedScope = !!(ctx && ctx.supportedApplication === false);

    function add(id) {
      if (CHECKS[id] && checks.indexOf(id) < 0) checks.push(id);
    }

    function addEvidence(type, weight, reason) {
      evidenceList.push(evidence(type, weight, reason));
    }

    var attentionGrid = resultHas(analysis, isGridHeaterResult);
    var attentionCcv = resultHas(analysis, isCcvConfigResult);
    var ccvOperating = resultHas(analysis, isCcvOperatingResult);
    var attentionLift = resultHas(analysis, function (r) { return isLiftPumpResult(r, ctx); });
    var attentionThrottle = resultHas(analysis, isThrottleConfigResult);
    var attentionEgr = resultHas(analysis, isEgrConfigResult);
    var attentionExhaustSensor = resultHas(analysis, isExhaustSensorConfigResult);
    var attentionDpf = resultHas(analysis, isDpfResult);
    var attentionScrModule = resultHas(analysis, isScrModuleResult);
    var attentionVgtNoSignal = resultHas(analysis, isVgtNoSignalResult);
    var attentionL5pFpcm = resultHas(analysis, function (r) { return isL5pFpcmResult(r, ctx); });
    var attentionL5pFpcmComm = resultHas(analysis, function (r) { return isL5pFpcmCommResult(r, ctx); });

    var circuitResults = resultList(analysis, function (r) { return !!r.circuitFault; });
    var ordinaryCircuitResults = circuitResults.filter(function (r) {
      return !isConfigurationAttentionResult(r, ctx) && !isL5pFpcmResult(r, ctx);
    });
    var hasCircuit = circuitResults.length > 0;
    var hasReference = resultHas(analysis, function (r) { return !!r.referenceVoltageFault; });
    var multipleCircuits = circuitResults.length >= 2;

    var needsLeaks = resultHas(analysis, function (r) {
      return r.checkLeaks === true;
    });
    var needsIntake = resultHas(analysis, function (r) {
      return !!r.checkIntakeConfiguration || !!r.hardwareRevisionPossible;
    });
    var needsVgt = resultHas(analysis, function (r) {
      return !!r.vgtRelevant && !r.circuitFault && !isVgtNoSignalResult(r);
    });
    var fuelChangeRelevant = resultHas(analysis, function (r) {
      return !!r.fuelFault && !!r.checkHardwareChanges && !isLiftPumpResult(r, ctx);
    });
    var exhaustRelevant = resultHas(analysis, function (r) {
      if (isExhaustSensorConfigResult(r) || isDpfResult(r) || isScrModuleResult(r)) return false;
      return !!r.exhaustRelevant || !!r.aftertreatmentFault;
    });
    var networkRelevant = resultHas(analysis, function (r) {
      return !!r.networkFault && !isL5pFpcmCommResult(r, ctx) &&
        !isExhaustSensorConfigResult(r) && !isScrModuleResult(r);
    });
    var securityRelevant = resultHas(analysis, function (r) { return !!r.securityFault; });
    var stockRequestedByCode = resultHas(analysis, function (r) { return r.stockTest === true; });
    var stockBlocked = resultHas(analysis, function (r) { return !!r.stockTestBlocked; });
    var mixedPerformanceSet = resultHas(analysis, function (r) {
      return !r.circuitFault && (
        r.performanceFault ||
        r.logRecommended ||
        r.boostFault ||
        r.fuelFault ||
        r.networkFault ||
        r.securityFault ||
        r.aftertreatmentFault
      );
    });
    var stockAllowed = stockRequestedByCode && !mixedPerformanceSet && supportsStockComparison(ctx && ctx.platform);
    var logRecommended = resultHas(analysis, function (r) { return !!r.logRecommended; });

    /*
      Shared electrical evidence outranks individual sensor diagnosis. Multiple
      circuit faults or a reference-voltage fault prompt the shared circuit
      check before any component-specific electrical inspection.
    */
    if (hasReference || multipleCircuits) {
      add('shared_electrical');
      addEvidence('vehicle', 4, 'Multiple circuit/reference-voltage faults point toward a shared power, ground, reference or harness issue.');
    }

    /* High-attention calibration/configuration checks come before generic circuit checks. */
    if (!limitedScope && attentionGrid) {
      add('grid_heater_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — grid-heater/intake-air-heater DTC present.');
    }
    if (!limitedScope && attentionCcv) {
      add('ccv_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — CCV/crankcase-pressure DTC present.');
    }
    if (!limitedScope && attentionLift) {
      add('lift_pump_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — lift-pump/low-pressure-fuel DTC present.');
    }
    if (!limitedScope && attentionThrottle) {
      add('throttle_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — throttle electrical/disconnected-type DTC present.');
    }
    if (!limitedScope && attentionEgr) {
      add('egr_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — EGR electrical/disconnected-type DTC present.');
    }
    if (!limitedScope && attentionExhaustSensor) {
      add('exhaust_sensor_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — exhaust/aftertreatment sensor electrical or communication DTC present.');
    }
    if (!limitedScope && attentionDpf) {
      add('dpf_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — DPF/exhaust-filter DTC present.');
    }
    if (!limitedScope && attentionScrModule) {
      add('scr_module_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — SCR/DEF/reductant control-module DTC present.');
    }
    if (!limitedScope && attentionVgtNoSignal) {
      add('vgt_configuration');
      flags.push('CALIBRATION CONFIGURATION ATTENTION — VGT signal/feedback fault present; verify whether the truck is intentionally non-VGT.');
    }

    if (!limitedScope && attentionL5pFpcm) {
      add('l5p_fpcm_fuel_gauge');
      flags.push('L5P FUEL PUMP DRIVER MODULE ATTENTION — verify whether the fuel gauge is also affected before choosing module-side versus CAN-line diagnosis.');
    }

    if (!limitedScope && ccvOperating) {
      addEvidence('vehicle', 4, 'CCV/crankcase-pressure operating or performance fault points toward the CCV system rather than an intentionally unplugged sensor/configuration request.');
      notes.push('A CCV operating/performance fault is being treated as a vehicle-side CCV-system issue, not as a routine tune-out/configuration check.');
    }

    if (limitedScope ? hasCircuit : (ordinaryCircuitResults.length || (hasCircuit && !attentionGrid && !attentionCcv && !attentionLift && !attentionThrottle && !attentionEgr && !attentionExhaustSensor && !attentionDpf && !attentionScrModule && !attentionVgtNoSignal && !attentionL5pFpcm))) {
      add('circuit_inspection');
    }

    if (hasCircuit) {
      addEvidence('vehicle', hasReference || multipleCircuits ? 2 : 4, 'Circuit high/low/open or related electrical fault is present.');
    }

    if (attentionL5pFpcm) {
      addEvidence('vehicle', 3, 'The L5P Fuel Pump Driver Control Module is a separately coded vehicle module and is not tuned by SFG; the fuel-gauge symptom check determines whether CAN-line diagnosis should be prioritized.');
    }

    /* Range/performance/plausibility faults are only weak vehicle-side evidence by themselves. */
    if (resultHas(analysis, function (r) { return !!r.performanceFault && !r.circuitFault; })) {
      addEvidence('vehicle', 1, 'A range/performance/plausibility fault is present; this alone does not establish a vehicle-side cause.');
    }

    if (needsLeaks) add('charge_air_leaks');
    if (!limitedScope && needsIntake) add('intake_configuration');
    if (!limitedScope && needsVgt) add('vgt_response');
    if (!limitedScope && fuelChangeRelevant) add('fuel_hardware_change');
    if (!limitedScope && exhaustRelevant) add('exhaust_configuration');
    if (networkRelevant) add('network_interference');
    if (securityRelevant && ctx && ctx.concern === 'no_start') add('security_history');

    /*
      Only HP Tuners and EZ Lynk get the routine return-to-stock comparison.
      EFILive never receives this generic stock-comparison question.
    */
    if (
      !limitedScope &&
      stockAllowed &&
      !stockBlocked &&
      ctx &&
      ctx.concern !== 'transmission'
    ) {
      add('stock_compatibility');
    }

    if (stockBlocked) {
      notes.push('Do not use the generic stock-calibration circuit-fault comparison on this vehicle/application.');
    }

    /* Do not blindly honor likelyVehicleSide on range/performance faults. */
    results.forEach(function (r) {
      if (r && r.likelyVehicleSide && !r.performanceFault && !r.circuitFault) {
        addEvidence('vehicle', 3, r.title || r.code || 'Vehicle-side evidence from DTC rule.');
      }
    });

    if (logRecommended) {
      if (ctx && ctx.platform === 'hp') follow = FOLLOW.HP_LOG;
      else if (ctx && ctx.platform === 'efi') follow = FOLLOW.EFI_LOG;
      else if (ctx && ctx.platform === 'ez') follow = FOLLOW.EZ_REQUEST;
    }

    var fuelPressurePerformance = results.some(function (r) {
      return (
        r.category === 'fuel_pressure' ||
        r.railPressureFault
      ) && !r.circuitFault;
    });

    if (fuelPressurePerformance) {
      if (ctx && ctx.platform === 'hp') follow = FOLLOW.HP_INFO_THEN_DATALOG;
      else if (ctx && ctx.platform === 'efi') follow = FOLLOW.EFI_LOG;
      else if (ctx && ctx.platform === 'ez') follow = FOLLOW.EZ_REQUEST;
    }

    if (ctx && ctx.concern === 'transmission' && !follow) {
      follow = platformFollow(ctx.platform || '', 'data');
    } else if (ctx && ctx.concern === 'running' && !results.length && !follow) {
      follow = platformFollow(ctx.platform || '', 'data');
    }

    status = resolveStatus(evidenceList, status);

    return {
      checks: unique(checks),
      notes: unique(notes),
      flags: unique(flags),
      evidence: evidenceList,
      status: status,
      follow: follow,
      logRecommended: !!follow,
      analysis: analysis || null
    };
  }

  function customerDtcTitle(result, ctx) {
    result = result || {};
    var code = String(result.code || '').toUpperCase();

    if (isL5p(ctx)) {
      if (code === 'P129F') return 'Fuel Pump Control / Lift Pump Fault';

      var l5pTitles = {
        U18A2: 'Lost Communication with Fuel Pump Driver Control Module',
        U2616: 'Fuel Pump Driver Control Module Lost Communication with ECM',
        P1000: 'Fuel Pump Driver Control Module Reset Count Message Counter Incorrect',
        P1009: 'Fuel Pump Driver Control Module Temperature High Signal Message Counter Incorrect',
        P129D: 'Fuel Pump Driver Control Module Ignition On/Start Switch Circuit Low Voltage',
        P12A6: 'Fuel Pump Driver Control Module Enable Circuit Performance',
        P12A8: 'Fuel Pump Control Status Signal Message Counter Incorrect'
      };
      if (l5pTitles[code]) return l5pTitles[code];
    }

    var genericTitle = !result.title || /manufacturer-specific/i.test(result.title);
    if (genericTitle) {
      if (isThrottleConfigResult(result)) return 'Throttle Control Electrical Fault';
      if (isEgrConfigResult(result)) return 'EGR Electrical / Control Fault';
      if (isExhaustSensorConfigResult(result)) return 'Exhaust / Aftertreatment Sensor Fault';
      if (isDpfResult(result)) return 'Diesel Particulate Filter / Exhaust Filter Fault';
      if (isScrModuleResult(result)) return 'SCR / Reductant Control Module Fault';
    }

    return result.title || '';
  }

  function customerDtcText(result, ctx) {
    result = result || {};
    var code = String(result.code || '').toUpperCase();

    /*
      SFG-specific customer wording for the L5P/L5D P1089 rule. Do not expose
      internal channel/parameter requirements and do not compare it to unrelated
      systems. HP still receives an Info Log first and a driving datalog after
      SFG supplies the correct channel list.
    */
    if (isVgtNoSignalResult(result) && ctx && ctx.nonVgt === true) {
      return 'This truck is intentionally non-VGT, so a VGT signal/feedback fault can indicate that the calibration is still expecting VGT feedback. SFG should review the calibration configuration before vehicle-side VGT wiring or actuator diagnosis is pursued.';
    }

    if (isGridHeaterResult(result) && ctx && ctx.gridHeaterAltered === true) {
      return 'The grid-heater/intake-air-heater setup is intentionally changed or disabled. Verify that the current tune was configured for that setup before treating this as a normal grid-heater electrical fault.';
    }

    if (isCcvConfigResult(result) && ctx && ctx.ccvAltered === true) {
      return 'The CCV/crankcase-pressure setup is intentionally changed or disabled. Verify that the current tune was configured for that setup before treating this as a normal sensor or wiring fault.';
    }

    if (isLiftPumpResult(result, ctx) && ctx && ctx.liftPumpAltered === true) {
      return 'The lift-pump/low-pressure-fuel setup is intentionally changed. Verify that the current tune was configured for that setup before treating this code as a normal stock-system fault.';
    }

    if (isThrottleConfigResult(result) && ctx && ctx.throttleAltered === true) {
      return 'The intake throttle/throttle valve is intentionally unplugged, removed or disabled. Because a related electrical fault remains active, SFG should verify that the calibration matches the ordered configuration.';
    }

    if (isEgrConfigResult(result) && ctx && ctx.egrAltered === true) {
      return 'The EGR system is intentionally unplugged, removed or disabled. Because a related electrical fault remains active, SFG should verify that the calibration matches the ordered configuration.';
    }

    if (isExhaustSensorConfigResult(result) && ctx && ctx.exhaustSensorAltered === true) {
      return 'The affected exhaust/aftertreatment sensor is intentionally unplugged or removed. Because the related fault remains active, SFG should verify that the calibration matches the ordered configuration.';
    }

    if (isDpfResult(result) && ctx && ctx.dpfAltered === true) {
      return 'The DPF/exhaust-filter system is intentionally removed or disabled. Because a related DTC remains active, SFG should verify that the calibration matches the ordered configuration.';
    }

    if (isScrModuleResult(result) && ctx && ctx.scrModuleAltered === true) {
      return 'The SCR/DEF/reductant control module is intentionally removed or disabled. Because a related module fault remains active, SFG should verify that the calibration matches the ordered configuration.';
    }

    if (isL5p(ctx) && code === 'P129F') {
      if (ctx && ctx.liftPumpAltered === true) {
        return 'On an L5P, this fault is associated with the factory lift-pump/fuel-pump-control system. Because the factory lift-pump setup has been intentionally changed, SFG should verify that the calibration/order configuration correctly accounts for that setup before the fault is treated as a normal stock-system problem.';
      }
      return 'On an L5P, this fault is associated with the factory lift-pump/fuel-pump-control system. If the factory lift pump has been unplugged or replaced for an aftermarket setup, the tune/order configuration needs to be checked; if the factory system is intact, diagnose the vehicle-side fuel-pump system.';
    }

    if (isL5pFpcmResult(result, ctx)) {
      if (ctx && ctx.fpcmGaugeIssue === true) {
        return 'This L5P Fuel Pump Driver Control Module fault is accompanied by a fuel-gauge problem. That combination strongly points toward a CAN line break or connection problem. Check the CAN plug connections at the NOx sensors and the main DEF tank connection.';
      }
      if (ctx && ctx.fpcmGaugeIssue === false) {
        return 'This is an isolated L5P Fuel Pump Driver Control Module fault with the fuel gauge reading normally. The driver module is separately coded and is not tuned by SFG, so this is more likely a vehicle/module-side issue.';
      }
      return 'The L5P Fuel Pump Driver Control Module is a separately coded module that SFG does not tune. Confirm whether the fuel gauge is also reading incorrectly; that symptom changes the next diagnostic step toward a CAN line break.';
    }

    if (isCcvOperatingResult(result)) {
      return 'This is a CCV/crankcase-pressure operating or performance fault rather than a simple unplugged-sensor condition. Diagnose the CCV system as a vehicle-side issue.';
    }

    if (code === 'P1089' && result.matchType === 'vehicle_exact') {
      if (ctx && ctx.platform === 'hp') {
        return 'This fault indicates a fuel-pressure-control problem. Common causes are related to fuel-pressure-control components. A driving datalog will be required for SFG review; create the ticket and Info Log first, and SFG will provide the correct datalog channel list.';
      }
      if (ctx && ctx.platform === 'ez') {
        return 'This fault indicates a fuel-pressure-control problem. Common causes are related to fuel-pressure-control components. A datalog will be required for SFG review; create the ticket first and SFG will request the required log.';
      }
      return 'This fault indicates a fuel-pressure-control problem. Common causes are related to fuel-pressure-control components. A datalog that captures the concern is required for SFG review.';
    }

    return result.customerText || '';
  }

  /* ==========================================================
     EFILIVE ERROR-DRIVEN PLAN
     ========================================================== */

  function efiNumeric(code) {
    var value = String(code || '').toUpperCase().replace(/^\$/, '');
    if (!/^[0-9A-F]{4}$/.test(value)) return null;
    return parseInt(value, 16);
  }

  function deriveEfiPlan(analysis, ctx) {
    var checks = [];
    var notes = [];
    var status = STATUS.READY;
    var resultLink = '';
    var resultLinkLabel = '';
    var results = analysis && analysis.results || [];

    function add(id) {
      if (CHECKS[id] && checks.indexOf(id) < 0) checks.push(id);
    }

    results.forEach(function (r) {
      var code = String(r.code || '').toUpperCase();
      var category = String(r.category || '').toLowerCase();
      var action = String(r.action || '').toLowerCase();
      var exact = r.matchType === 'exact';

      /*
        Only exact known EFILive entries are allowed to create additional
        questions. Family/range fallbacks may classify an error for the ticket,
        but they do not invent firmware, BBX or recovery checks.
      */
      if (!exact) return;

      var firmwareRelevant = (
        category.indexOf('firmware') >= 0 ||
        category.indexOf('boot') >= 0 ||
        code === '$0030'
      );

      var bbxRelevant = (
        category.indexOf('bbx') >= 0 ||
        code === '$050C' ||
        /^\$0A0[1-7]$/.test(code) ||
        /^\$0A1[1-4]$/.test(code) ||
        /^\$0A2[1-4]$/.test(code)
      );

      var vehicleCommRelevant = (
        category.indexOf('obd') >= 0 ||
        code === '$0280' ||
        code === '$0281'
      );

      var networkRelevant = (
        code === '$054A' ||
        category.indexOf('network') >= 0
      );

      var vendorRelevant = (
        (action.indexOf('efilive') >= 0 && action.indexOf('contact') >= 0) ||
        r.vendorSupport === true
      );

      if (firmwareRelevant) add('efi_firmware');
      if (bbxRelevant) add('efi_bbx');
      if (vehicleCommRelevant) add('ignition_on');
      if (networkRelevant) add('network_interference');

      if (vendorRelevant) {
        resultLink = URLS.efiSupport;
        resultLinkLabel = 'Open EFILive Support';
      }

      /*
        An error code by itself does not establish that programming recovery is
        required. Failed-write stage is the authority. Exact database entries
        may still flag recovery, but only while handling a write operation.
        $054A specifically remains a network/bootloader-start diagnostic unless
        the write-stage context independently establishes a recovery state.
      */
      if (
        ctx && ctx.operation === 'write' &&
        r.programmingRecovery === true &&
        code !== '$054A'
      ) {
        status = STATUS.RECOVERY;
      }
    });

    /* Failed writes outrank generic error-family guidance. */
    if (ctx && ctx.operation === 'write' && ctx.writeFailedAfterErase) {
      status = STATUS.RECOVERY;
    }

    return {
      checks: unique(checks),
      notes: unique(notes),
      status: status,
      resultLink: resultLink,
      resultLinkLabel: resultLinkLabel,
      analysis: analysis || null
    };
  }

  /* ==========================================================
     FLOW NODES
     ========================================================== */

  var NODES = {
    vehicle_vin: {
      id: 'vehicle_vin',
      type: 'vin',
      label: 'Enter the vehicle VIN.',
      description: 'The VIN lets Support identify the vehicle and engine/application automatically and removes questions the VIN already answers.',
      allowWithoutVin: true,
      manualNext: 'vehicle_manual',
      next: 'vehicle_scope'
    },

    vehicle_scope: {
      id: 'vehicle_scope',
      type: 'router',
      route: function (ctx) {
        return ctx && ctx.supportedApplication === false ? 'vehicle_scope_notice' : 'vehicle_platform';
      }
    },

    vehicle_scope_notice: {
      id: 'vehicle_scope_notice',
      type: 'choice',
      label: 'This vehicle is outside the applications covered by SFG’s vehicle-specific automated tuning support.',
      description: 'Generic DTC interpretation can still be used, but vehicle-specific SFG tuning assumptions and platform-specific diagnostic conclusions will be limited.',
      options: [
        {
          value: 'limited',
          label: 'Continue with limited troubleshooting',
          next: 'vehicle_platform',
          meta: {
            flag: 'OUTSIDE AUTOMATED TUNING SCOPE — use generic diagnostic information only; do not treat vehicle-specific SFG tuning rules as validated for this application.',
            note: 'This vehicle is outside SFG’s vehicle-specific automated tuning-support coverage. Generic diagnostic information may still be useful.'
          }
        },
        {
          value: 'ticket',
          label: 'Create General Support Ticket',
          next: 'RESULT',
          meta: {
            status: STATUS.GENERAL,
            flag: 'OUTSIDE AUTOMATED TUNING SCOPE',
            note: 'Vehicle is outside the applications covered by SFG’s vehicle-specific automated tuning support.'
          }
        }
      ]
    },

    vehicle_manual: {
      id: 'vehicle_manual',
      type: 'text',
      label: 'What vehicle are we working with?',
      description: 'VIN lookup was skipped. Include year, make, model and engine.',
      placeholder: 'Example: 2020 Chevrolet Silverado 2500HD, 6.6L L5P',
      next: 'vehicle_platform'
    },

    vehicle_platform: {
      id: 'vehicle_platform',
      type: 'choice',
      label: 'Which tuning platform is being used?',
      options: [
        { value: 'hp', label: 'HP Tuners', next: 'vehicle_concern', meta: { platform: 'hp' } },
        { value: 'efi', label: 'EFILive', next: 'vehicle_concern', meta: { platform: 'efi' } },
        { value: 'ez', label: 'EZ Lynk', next: 'vehicle_concern', meta: { platform: 'ez' } },
        { value: 'other', label: 'Other / Not sure', next: 'vehicle_concern', meta: { platform: 'other' } }
      ]
    },

    vehicle_concern: {
      id: 'vehicle_concern',
      type: 'choice',
      label: 'What best describes the concern?',
      options: [
        { value: 'running', label: 'Engine running / power / boost / smoke issue', next: 'vehicle_dtcs', meta: { concern: 'running' } },
        { value: 'no_start', label: 'No-start / hard-start', next: 'vehicle_dtcs', meta: { concern: 'no_start' } },
        { value: 'transmission', label: 'Transmission / shifting / converter issue', next: 'vehicle_dtcs', meta: { concern: 'transmission' } },
        { value: 'power_level', label: 'Power level / SOTF not changing correctly', next: 'power_level_file', meta: { concern: 'power_level' } },
        { value: 'other', label: 'Fault Codes / Something Else', next: 'vehicle_dtcs', meta: { concern: 'other' } }
      ]
    },

    vehicle_dtcs: {
      id: 'vehicle_dtcs',
      type: 'dtc',
      label: 'Enter every diagnostic trouble code currently present.',
      description: 'Paste all current codes together. Enter NONE if the vehicle has been scanned and no codes are present.',
      placeholder: 'Example: P0101 P0299 P0087 — or NONE',
      requireScanned: true,
      next: 'vehicle_dynamic'
    },

    vehicle_dynamic: {
      id: 'vehicle_dynamic',
      type: 'dynamic_vehicle_checks'
    },

    no_start_base: {
      id: 'no_start_base',
      type: 'check_sequence',
      checks: ['full_flash', 'weak_crank', 'required_connectors', 'security_history'],
      next: 'RESULT'
    },

    transmission_base: {
      id: 'transmission_base',
      type: 'check_sequence',
      checks: ['transmission_built'],
      next: 'RESULT',
      meta: { followByPlatform: true }
    },

    /* ---------- READ / FLASH ---------- */

    flash_vin: {
      id: 'flash_vin',
      type: 'vin',
      label: 'Enter the vehicle VIN.',
      allowWithoutVin: true,
      manualNext: 'flash_manual',
      next: 'flash_platform'
    },

    flash_manual: {
      id: 'flash_manual',
      type: 'text',
      label: 'What vehicle are we working with?',
      placeholder: 'Year, make, model and engine',
      next: 'flash_platform'
    },

    flash_platform: {
      id: 'flash_platform',
      type: 'choice',
      label: 'Which tuning platform is being used?',
      options: [
        { value: 'hp', label: 'HP Tuners', next: 'hp_operation', meta: { platform: 'hp' } },
        { value: 'efi', label: 'EFILive', next: 'efi_operation', meta: { platform: 'efi' } },
        { value: 'ez', label: 'EZ Lynk', next: 'ez_operation', meta: { platform: 'ez' } },
        { value: 'other', label: 'Other / Not sure', next: 'flash_other', meta: { platform: 'other' } }
      ]
    },

    hp_operation: {
      id: 'hp_operation',
      type: 'choice',
      label: 'What operation is failing?',
      options: [
        { value: 'remote_file', label: 'Opening a tune file — Remote Tuning network error', next: 'hp_remote_tuning_checks', meta: { operation: 'file_open' } },
        { value: 'read', label: 'Reading the vehicle', next: 'hp_read_checks', meta: { operation: 'read' } },
        { value: 'write', label: 'Writing / flashing the vehicle', next: 'hp_write_controller', meta: { operation: 'write' } },
        { value: 'both', label: 'Both reading and writing', next: 'hp_write_controller', meta: { operation: 'write' } }
      ]
    },

    hp_remote_tuning_checks: {
      id: 'hp_remote_tuning_checks',
      type: 'check_sequence',
      description: 'For the HP Tuners Remote Tuning error “Unable to open file. This file is part of a Remote Tuning network that you are not part of.”',
      checks: ['hp_remote_correct_mpvi', 'hp_beta', 'hp_remote_fresh_download'],
      next: 'RESULT'
    },

    hp_read_checks: {
      id: 'hp_read_checks',
      type: 'check_sequence',
      checks: ['hp_beta', 'hp_firmware', 'hp_internet', 'network_interference'],
      next: 'hp_exact_error',
      meta: { follow: FOLLOW.HP_INFO }
    },

    hp_write_controller: {
      id: 'hp_write_controller',
      type: 'choice',
      label: 'Has the ECM/PCM/TCM being flashed been replaced since this tune/order was created?',
      description: 'Do not flash the existing tune into a replacement controller until the file/controller combination is reviewed.',
      options: [
        { value: 'no', label: 'No', next: 'hp_write_checks' },
        {
          value: 'oem',
          label: 'Yes — OEM-equivalent replacement',
          next: 'RESULT',
          meta: {
            status: STATUS.CONTROLLER,
            follow: FOLLOW.HP_INFO,
            note: 'Do not flash the existing tune. Create a ticket and provide a new stock read so SFG can update the file for the replacement controller.'
          }
        },
        {
          value: 'different',
          label: 'Yes — different controller/model swap',
          next: 'RESULT',
          meta: {
            status: STATUS.REVISION,
            follow: FOLLOW.HP_INFO,
            note: 'Do not flash the existing tune. A controller/model swap requires SFG review and may require a tune revision if supported.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          next: 'RESULT',
          meta: {
            status: STATUS.CONTROLLER,
            follow: FOLLOW.HP_INFO,
            note: 'Controller replacement status is uncertain. Do not attempt another flash until SFG reviews the controller/file combination.'
          }
        }
      ]
    },

    hp_write_checks: {
      id: 'hp_write_checks',
      type: 'check_sequence',
      checks: ['hp_beta', 'hp_firmware', 'hp_internet', 'battery_support'],
      next: 'hp_write_stage'
    },

    hp_write_stage: {
      id: 'hp_write_stage',
      type: 'choice',
      label: 'At what point does the write fail?',
      options: [
        { value: 'before_erase', label: 'Before erase begins', next: 'hp_preerase_network', meta: { follow: FOLLOW.HP_INFO } },
        { value: 'during_erase', label: 'During erase', next: 'hp_recovery_network', meta: { status: STATUS.RECOVERY, writeFailedAfterErase: true } },
        { value: 'after_erase', label: 'After erase, when writing begins', next: 'hp_recovery_network', meta: { status: STATUS.RECOVERY, writeFailedAfterErase: true } },
        { value: 'during_write', label: 'During writing', next: 'hp_recovery_network', meta: { status: STATUS.RECOVERY, writeFailedAfterErase: true } },
        {
          value: 'verification',
          label: 'During verification',
          next: 'hp_exact_error',
          meta: {
            follow: FOLLOW.HP_INFO,
            note: 'The failure occurred during verification. SFG should review the exact error and Info Log.'
          }
        },
        { value: 'unsure', label: 'Not sure', next: 'hp_preerase_network', meta: { follow: FOLLOW.HP_INFO } }
      ]
    },

    hp_preerase_network: {
      id: 'hp_preerase_network',
      type: 'check_sequence',
      checks: ['network_interference'],
      next: 'hp_exact_error'
    },

    hp_recovery_network: {
      id: 'hp_recovery_network',
      type: 'check_sequence',
      checks: ['network_interference'],
      next: 'hp_stock_recovery'
    },

    hp_stock_recovery: {
      id: 'hp_stock_recovery',
      type: 'choice',
      label: 'After isolating likely network interference, have you attempted Recover / Write Entire using the correct stock file?',
      options: [
        {
          value: 'failed',
          label: 'Yes — recovery still fails',
          next: 'hp_exact_error',
          meta: {
            status: STATUS.RECOVERY,
            follow: FOLLOW.HP_INFO,
            note: 'Programming recovery is required. Do not continue experimenting with unrelated files or settings.'
          }
        },
        {
          value: 'succeeded',
          label: 'Yes — stock recovery succeeded',
          next: 'RESULT',
          meta: {
            follow: FOLLOW.HP_INFO,
            note: 'Stock recovery succeeded. If the original tuning problem remains, create a ticket before attempting further modified flashes.'
          }
        },
        {
          value: 'not_attempted',
          label: 'Not attempted yet',
          stop: {
            title: 'Attempt stock recovery first',
            text: 'After isolating network interference, use Recover / Write Entire with the correct stock file. If it still fails, return and create a programming-recovery ticket.'
          }
        }
      ]
    },

    hp_exact_error: {
      id: 'hp_exact_error',
      type: 'text',
      label: 'Enter the exact HP Tuners error message.',
      placeholder: 'Copy the message exactly and include where in the process it occurs.',
      next: 'RESULT',
      meta: { follow: FOLLOW.HP_INFO }
    },

    /* ---------- EFILIVE READ / FLASH ---------- */

    efi_operation: {
      id: 'efi_operation',
      type: 'choice',
      label: 'What operation is failing?',
      options: [
        { value: 'read', label: 'Reading the vehicle', next: 'efi_error', meta: { operation: 'read' } },
        { value: 'write', label: 'Writing / flashing the vehicle', next: 'efi_write_battery', meta: { operation: 'write' } },
        { value: 'both', label: 'Both reading and writing', next: 'efi_write_battery', meta: { operation: 'write' } }
      ]
    },

    efi_write_battery: {
      id: 'efi_write_battery',
      type: 'check_sequence',
      checks: ['battery_support'],
      next: 'efi_write_stage'
    },

    efi_write_stage: {
      id: 'efi_write_stage',
      type: 'choice',
      label: 'At what point does the write fail?',
      options: [
        { value: 'before_erase', label: 'Before erase begins', next: 'efi_error', meta: { writeFailedAfterErase: false } },
        { value: 'during_erase', label: 'During erase', next: 'efi_lb7_power_router', meta: { writeFailedAfterErase: true, status: STATUS.RECOVERY } },
        { value: 'after_erase', label: 'After erase / during writing', next: 'efi_lb7_power_router', meta: { writeFailedAfterErase: true, status: STATUS.RECOVERY } },
        { value: 'verification', label: 'During verification', next: 'efi_error' },
        { value: 'unsure', label: 'Not sure', next: 'efi_lb7_power_router', meta: { writeFailedAfterErase: true, status: STATUS.RECOVERY } }
      ]
    },

    efi_lb7_power_router: {
      id: 'efi_lb7_power_router',
      type: 'router',
      route: function (ctx) {
        return isLb7Lly(ctx) ? 'efi_lb7_power' : 'efi_error';
      }
    },

    efi_lb7_power: {
      id: 'efi_lb7_power',
      type: 'choice',
      label: 'Has the controller remained powered continuously since the failed write?',
      description: 'For an LB7/LLY failed write, controller power state determines the recovery procedure.',
      options: [
        {
          value: 'powered',
          label: 'Yes — controller power has remained on',
          next: 'RESULT',
          meta: {
            status: STATUS.RETRY_FLASH,
            note: 'DO NOT REMOVE POWER FROM THE CONTROLLER. As long as the controller remains powered up, EFILive’s proprietary boot loader running in the controller will continue to wait for a successful full flash procedure. Retry the full flash procedure until it is successful.'
          }
        },
        {
          value: 'power_lost',
          label: 'No — ignition/controller power was turned off or lost',
          next: 'RESULT',
          meta: {
            status: STATUS.RECOVERY,
            note: 'The LB7/LLY controller lost power after the failed write. A third-party bench recovery service is likely required before the controller can be programmed again. SFG does not provide this service.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          next: 'RESULT',
          meta: {
            status: STATUS.RECOVERY,
            note: 'Controller power state after the LB7/LLY failed write is uncertain. Do not continue experimenting. The controller may require a third-party bench recovery service if power was removed.'
          }
        }
      ]
    },

    efi_error: {
      id: 'efi_error',
      type: 'efilive_error',
      label: 'Enter the exact EFILive error code and message.',
      description: 'Paste the $xxxx code and exact message. The troubleshooter will only ask follow-up questions relevant to that error.',
      placeholder: 'Example: $054A plus the exact message',
      next: 'efi_dynamic'
    },

    efi_dynamic: {
      id: 'efi_dynamic',
      type: 'dynamic_efi_checks'
    },

    /* ---------- EZ LYNK READ / FLASH ---------- */

    ez_operation: {
      id: 'ez_operation',
      type: 'choice',
      label: 'What operation is failing?',
      options: [
        { value: 'connect', label: 'Connecting / reading vehicle information', next: 'ez_connect_checks', meta: { operation: 'read' } },
        { value: 'write', label: 'Installing / flashing a tune', next: 'ez_write_checks', meta: { operation: 'write' } },
        { value: 'other', label: 'Something else', next: 'ez_connect_checks', meta: { operation: 'other' } }
      ]
    },

    ez_connect_checks: {
      id: 'ez_connect_checks',
      type: 'choice',
      label: 'Do you have stable internet access?',
      description: 'EZ Lynk requires stable internet connectivity.',
      options: [
        { value: 'yes', label: 'Yes', next: 'ez_error' },
        {
          value: 'no',
          label: 'No / Not sure',
          stop: {
            title: 'Use stable internet first',
            text: 'Restore reliable internet connectivity and retry the EZ Lynk operation.'
          }
        }
      ]
    },

    ez_write_checks: {
      id: 'ez_write_checks',
      type: 'check_sequence',
      checks: ['battery_support'],
      next: 'ez_connect_checks'
    },

    ez_error: {
      id: 'ez_error',
      type: 'text',
      label: 'Enter the exact EZ Lynk error or symptom.',
      next: 'ez_vendor_prompt'
    },

    ez_vendor_prompt: {
      id: 'ez_vendor_prompt',
      type: 'choice',
      label: 'Does the EZ Lynk app specifically instruct you to contact EZ Lynk Support?',
      options: [
        {
          value: 'yes',
          label: 'Yes',
          next: 'RESULT',
          meta: {
            status: STATUS.GENERAL,
            note: 'The EZ Lynk app is directing this issue to EZ Lynk Support. Use the official EZ Lynk support channel first; an SFG ticket is still available if the concern also involves an SFG order/tune.',
            resultLink: URLS.ezSupport,
            resultLinkLabel: 'Open EZ Lynk Support'
          }
        },
        { value: 'no', label: 'No', next: 'RESULT', meta: { follow: FOLLOW.EZ_REQUEST } },
        { value: 'unsure', label: 'Not sure', next: 'RESULT', meta: { follow: FOLLOW.EZ_REQUEST } }
      ]
    },

    flash_other: {
      id: 'flash_other',
      type: 'text',
      label: 'Describe the read / flash problem and identify the tuning tool if possible.',
      next: 'RESULT'
    },

    /* ---------- DEVICE CONNECTION ---------- */

    device_platform: {
      id: 'device_platform',
      type: 'choice',
      label: 'Which tuning platform/device is having trouble connecting?',
      options: [
        { value: 'hp', label: 'HP Tuners', next: 'hp_connection_where', meta: { platform: 'hp' } },
        { value: 'efi', label: 'EFILive', next: 'efi_connection_where', meta: { platform: 'efi' } },
        { value: 'ez', label: 'EZ Lynk', next: 'ez_connection_power', meta: { platform: 'ez' } },
        { value: 'other', label: 'Other / Not sure', next: 'device_other', meta: { platform: 'other' } }
      ]
    },

    hp_connection_where: {
      id: 'hp_connection_where',
      type: 'choice',
      label: 'Where does the HP Tuners connection fail?',
      options: [
        { value: 'computer', label: 'The computer does not recognize the HP Tuners interface', next: 'hp_pc_checks' },
        { value: 'vehicle', label: 'The computer recognizes the interface, but it will not communicate with the vehicle', next: 'device_vehicle_vin' },
        {
          value: 'unsure',
          label: 'Not sure',
          infoBack: {
            title: 'Check whether VCM Suite recognizes the interface',
            text: 'In your current VCM Editor or VCM Scanner window, select Help > Resync Interface. If the interface is recognized, the resync will run and VCM Suite will show the result at the bottom of the window. If the interface is not recognized, it will display \"Please connect an MPVI interface\". Use that result to return to the previous question and choose where the connection is failing.',
            button: 'Back to Connection Question'
          }
        }
      ]
    },

    hp_pc_checks: {
      id: 'hp_pc_checks',
      type: 'check_sequence',
      checks: ['hp_beta'],
      next: 'hp_pc_multiple_windows'
    },

    hp_pc_multiple_windows: {
      id: 'hp_pc_multiple_windows',
      type: 'choice',
      label: 'Are multiple HP Tuners / VCM Suite windows open on the computer?',
      description: 'Before moving to USB troubleshooting, rule out more than one VCM Scanner, VCM Editor or other VCM Suite window trying to access the interface.',
      options: [
        {
          value: 'yes',
          label: 'Yes — multiple HP Tuners / VCM Suite windows are open',
          next: 'hp_pc_single_window_retry'
        },
        {
          value: 'no',
          label: 'No — only one HP Tuners / VCM Suite window is open',
          next: 'hp_pc_usb'
        },
        {
          value: 'unsure',
          label: 'Not sure',
          next: 'hp_pc_check_windows'
        }
      ]
    },

    hp_pc_check_windows: {
      id: 'hp_pc_check_windows',
      type: 'choice',
      label: 'Check the open HP Tuners / VCM Suite programs now. Are multiple VCM Suite windows open?',
      description: 'Check for VCM Scanner, VCM Editor or other VCM Suite windows. If multiple are open, close every VCM Suite program before starting again with one fresh window so a stale interface connection is not carried forward.',
      options: [
        {
          value: 'multiple',
          label: 'Yes — I found multiple VCM Suite windows open',
          next: 'hp_pc_single_window_retry'
        },
        {
          value: 'single',
          label: 'No — only one or no VCM Suite window was open',
          next: 'hp_pc_usb'
        }
      ]
    },

    hp_pc_single_window_retry: {
      id: 'hp_pc_single_window_retry',
      type: 'choice',
      label: 'Close every HP Tuners / VCM Suite program, then open one fresh VCM Suite window and retry the interface. Is the interface recognized now?',
      description: 'Closing every VCM Suite program before reopening one fresh window clears any stale interface session held by another VCM Suite process.',
      options: [
        {
          value: 'yes',
          label: 'Yes — the interface is recognized now',
          next: 'hp_pc_resolved'
        },
        {
          value: 'no',
          label: 'No — it is still not recognized',
          next: 'hp_pc_usb'
        }
      ]
    },

    hp_pc_usb: {
      id: 'hp_pc_usb',
      type: 'choice',
      label: 'After trying a known-good cable, another USB port and another computer if practical, is the interface still not recognized?',
      options: [
        {
          value: 'yes',
          label: 'Yes — still not recognized',
          next: 'RESULT',
          meta: {
            status: STATUS.GENERAL,
            note: 'The HP Tuners interface is not being recognized by the computer after the VCM Suite BETA, single-window, cable/USB and alternate-computer checks. Use HP Tuners Support for interface/computer support.',
            resultLink: URLS.hpSupport,
            resultLinkLabel: 'Open HP Tuners Support'
          }
        },
        { value: 'no', label: 'No — it is recognized now', next: 'hp_pc_resolved' }
      ]
    },

    hp_pc_resolved: {
      id: 'hp_pc_resolved',
      type: 'resolved',
      title: 'HP Tuners interface recognized',
      text: 'The interface is recognized by the computer now, so this connection problem is resolved. No support ticket is needed for this issue.'
    },

    device_vehicle_vin: {
      id: 'device_vehicle_vin',
      type: 'vin',
      label: 'Enter the vehicle VIN.',
      description: 'VIN becomes useful here because the interface is recognized and the problem is now vehicle communication.',
      allowWithoutVin: true,
      manualNext: 'device_vehicle_manual',
      next: 'hp_vehicle_comm_checks'
    },

    device_vehicle_manual: {
      id: 'device_vehicle_manual',
      type: 'text',
      label: 'What vehicle are we working with?',
      placeholder: 'Year, make, model and engine',
      next: 'hp_vehicle_comm_checks'
    },

    hp_vehicle_comm_checks: {
      id: 'hp_vehicle_comm_checks',
      type: 'check_sequence',
      checks: ['ignition_on', 'obd_power_network'],
      next: 'RESULT',
      meta: {
        follow: FOLLOW.HP_INFO,
        note: 'The interface is recognized by the computer but vehicle communication still fails. Include an HP Tuners Info Log with the ticket.'
      }
    },

    efi_connection_where: {
      id: 'efi_connection_where',
      type: 'choice',
      label: 'Where does the EFILive connection fail?',
      options: [
        { value: 'computer', label: 'The computer does not recognize the AutoCal/FlashScan', next: 'efi_pc_usb' },
        { value: 'vehicle', label: 'The device is recognized, but vehicle communication fails', next: 'efi_vehicle_vin' }
      ]
    },

    efi_pc_usb: {
      id: 'efi_pc_usb',
      type: 'choice',
      label: 'After trying a known-good cable and another USB port, is the AutoCal/FlashScan still not recognized?',
      options: [
        {
          value: 'yes',
          label: 'Yes — still not recognized',
          next: 'RESULT',
          meta: {
            status: STATUS.GENERAL,
            note: 'The EFILive device is not recognized by the computer after the relevant USB checks. Use EFILive Support for device/computer support.',
            resultLink: URLS.efiSupport,
            resultLinkLabel: 'Open EFILive Support'
          }
        },
        { value: 'no', label: 'No — it is recognized now', next: 'RESULT' }
      ]
    },

    efi_vehicle_vin: {
      id: 'efi_vehicle_vin',
      type: 'vin',
      label: 'Enter the vehicle VIN.',
      allowWithoutVin: true,
      manualNext: 'efi_vehicle_manual',
      next: 'efi_vehicle_error'
    },

    efi_vehicle_manual: {
      id: 'efi_vehicle_manual',
      type: 'text',
      label: 'What vehicle are we working with?',
      placeholder: 'Year, make, model and engine',
      next: 'efi_vehicle_error'
    },

    efi_vehicle_error: {
      id: 'efi_vehicle_error',
      type: 'efilive_error',
      label: 'Enter the EFILive error code/message shown during vehicle communication.',
      placeholder: 'Example: $0280 plus the exact message',
      next: 'efi_dynamic'
    },

    ez_connection_power: {
      id: 'ez_connection_power',
      type: 'choice',
      label: 'Does the AutoAgent power up when connected to the vehicle?',
      options: [
        { value: 'yes', label: 'Yes', next: 'ez_connection_internet' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Check OBD power first',
            text: 'The AutoAgent needs vehicle OBD power. Check the applicable OBD power/fuse issue before continuing.'
          }
        },
        { value: 'unsure', label: 'Not sure', next: 'ez_connection_internet' }
      ]
    },

    ez_connection_internet: {
      id: 'ez_connection_internet',
      type: 'choice',
      label: 'Does the phone have stable internet connectivity?',
      options: [
        { value: 'yes', label: 'Yes', next: 'ez_connection_restart' },
        {
          value: 'no',
          label: 'No / Not sure',
          stop: {
            title: 'Restore stable connectivity first',
            text: 'Restore stable phone/internet connectivity and retry the AutoAgent.'
          }
        }
      ]
    },

    ez_connection_restart: {
      id: 'ez_connection_restart',
      type: 'choice',
      label: 'Have you restarted the app/phone and reconnected the AutoAgent?',
      options: [
        {
          value: 'yes',
          label: 'Yes — it still fails',
          next: 'RESULT',
          meta: {
            note: 'The AutoAgent is powered and connectivity basics have been checked. Create an SFG ticket or use EZ Lynk Support if the issue appears device/account related.',
            resultLink: URLS.ezSupport,
            resultLinkLabel: 'Open EZ Lynk Support'
          }
        },
        {
          value: 'no',
          label: 'No / Not sure',
          stop: {
            title: 'Restart and reconnect first',
            text: 'Restart the app/phone, reconnect the AutoAgent, verify app permissions/connectivity, and retry.'
          }
        }
      ]
    },

    device_other: {
      id: 'device_other',
      type: 'text',
      label: 'Describe the device connection problem and identify the device if possible.',
      next: 'RESULT'
    },

    /* ---------- LICENSING ---------- */

    licensing_platform: {
      id: 'licensing_platform',
      type: 'choice',
      label: 'Which platform is the licensing / credit question about?',
      options: [
        { value: 'hp', label: 'HP Tuners', next: 'hp_licensing_type', meta: { platform: 'hp' } },
        { value: 'efi', label: 'EFILive', next: 'efi_licensing_type', meta: { platform: 'efi' } },
        { value: 'ez', label: 'EZ Lynk', next: 'ez_licensing', meta: { platform: 'ez' } },
        { value: 'other', label: 'Other / Not sure', next: 'licensing_other', meta: { platform: 'other' } }
      ]
    },

    hp_licensing_type: {
      id: 'hp_licensing_type',
      type: 'choice',
      label: 'What best describes the HP Tuners licensing/credit question?',
      options: [
        { value: 'credits_missing', label: 'Purchased credits are not showing for a first-time flash', next: 'hp_resync' },
        {
          value: 'licensed_again',
          label: 'A previously licensed vehicle is asking for credits again',
          next: 'RESULT',
          meta: {
            follow: FOLLOW.HP_INFO,
            note: 'A previously licensed vehicle asking for credits again requires SFG review. Include an HP Tuners Info Log with the ticket.'
          }
        },
        {
          value: 'quantity',
          label: 'How many credits does this vehicle/controller require?',
          next: 'RESULT',
          meta: {
            note: 'Use the current HP Tuners vehicle support page to confirm credit requirements.',
            resultLink: URLS.hpCredits,
            resultLinkLabel: 'Open HP Tuners Credit Chart'
          }
        },
        { value: 'other', label: 'Other licensing / credit issue', next: 'licensing_other' }
      ]
    },

    hp_resync: {
      id: 'hp_resync',
      type: 'choice',
      label: 'Have you used Help > Resync Interface in VCM Suite?',
      options: [
        { value: 'yes', label: 'Yes — credits are still missing', next: 'hp_mpvi_serial' },
        {
          value: 'no',
          label: 'No / Not sure',
          stop: {
            title: 'Resync the interface first',
            text: 'Use Help > Resync Interface in VCM Suite, then recheck the credit balance.'
          }
        }
      ]
    },

    hp_mpvi_serial: {
      id: 'hp_mpvi_serial',
      type: 'text',
      label: 'Enter the MPVI serial number.',
      description: 'SFG needs the interface serial when purchased credits still do not appear after Resync Interface.',
      next: 'RESULT'
    },

    efi_licensing_type: {
      id: 'efi_licensing_type',
      type: 'choice',
      label: 'What best describes the EFILive licensing issue?',
      options: [
        { value: 'vin_license', label: 'VIN license will not activate', next: 'efi_license_serial_match' },
        {
          value: 'linked_other',
          label: 'AutoCal is linked to another tuner',
          next: 'RESULT',
          meta: {
            note: 'Use the unlink/linking section in the SFG EFILive instructions.',
            resultLink: URLS.efiInstructions,
            resultLinkLabel: 'Open EFILive Instructions'
          }
        },
        { value: 'other', label: 'Other EFILive licensing issue', next: 'licensing_other' }
      ]
    },

    efi_license_serial_match: {
      id: 'efi_license_serial_match',
      type: 'choice',
      label: 'Does the AutoCal serial number match the AutoCal serial associated with the order / VIN license?',
      description: 'A VIN license generated for a different AutoCal will not activate on the current AutoCal.',
      options: [
        {
          value: 'yes',
          label: 'Yes — the serial matches',
          next: 'RESULT',
          meta: {
            note: 'The AutoCal serial matches the order/license assignment, but the VIN license still will not activate. SFG should review the license/order.'
          }
        },
        {
          value: 'no',
          label: 'No — the serial does not match',
          next: 'RESULT',
          meta: {
            flag: 'EFILIVE LICENSE ASSIGNMENT CHECK — AutoCal serial does not match the device associated with the order/license.',
            note: 'The VIN license is associated with a different AutoCal serial. A license made for another AutoCal will not activate on this device. Create a ticket with the current AutoCal serial and order number so SFG can review the assignment.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Verify the AutoCal serial first',
            text: 'Find the AutoCal serial number and compare it with the AutoCal serial associated with the order/license. Do not troubleshoot auth code, linking or controller setup until the device serial is confirmed.',
            url: URLS.efiInstructions,
            button: 'Open EFILive Instructions'
          }
        }
      ]
    },

    ez_licensing: {
      id: 'ez_licensing',
      type: 'choice',
      label: 'EZ Lynk account/licensing issues are normally handled by EZ Lynk Support first.',
      options: [
        { value: 'ticket', label: 'Create SFG ticket', next: 'RESULT', meta: { status: STATUS.GENERAL } },
        {
          value: 'vendor',
          label: 'I will contact EZ Lynk Support',
          next: 'RESULT',
          meta: {
            status: STATUS.GENERAL,
            note: 'Use EZ Lynk Support first for account/licensing issues.',
            resultLink: URLS.ezSupport,
            resultLinkLabel: 'Open EZ Lynk Support'
          }
        }
      ]
    },

    licensing_other: {
      id: 'licensing_other',
      type: 'text',
      label: 'Describe the licensing or credit issue.',
      next: 'RESULT'
    },

    /* ---------- POWER LEVEL / SOTF (UNDER VEHICLE PROBLEM) ---------- */

    power_level_file: {
      id: 'power_level_file',
      type: 'choice',
      label: 'Is the correct SOTF / power-level-enabled tune file currently loaded in the vehicle?',
      description: 'Confirm the intended file is installed before diagnosing the control method or assuming a requested feature is missing from the calibration.',
      options: [
        { value: 'yes', label: 'Yes', next: 'power_level_control_router' },
        {
          value: 'no',
          label: 'No',
          stop: {
            title: 'Load the correct power-level-enabled file first',
            text: 'The vehicle cannot change tuning levels unless the correct SOTF / power-level-enabled file is loaded. Install the intended file and retry.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Confirm the current tune file first',
            text: 'Verify that the currently installed file is the intended SOTF / power-level-enabled file before continuing.'
          }
        }
      ]
    },

    power_level_control_router: {
      id: 'power_level_control_router',
      type: 'router',
      route: function (ctx) {
        var method = powerLevelControlMethod(ctx || {});
        if (method === 'cruise') return 'power_level_cruise_buttons';
        if (method === 'physical_switch') return 'power_level_switch_wiring';
        return 'power_level_control_unknown';
      }
    },

    power_level_control_unknown: {
      id: 'power_level_control_unknown',
      type: 'choice',
      label: 'How are power levels changed on this vehicle?',
      options: [
        { value: 'cruise', label: 'Cruise-control buttons', next: 'power_level_cruise_buttons', meta: { controlMethod: 'cruise' } },
        { value: 'switch', label: 'Physical SOTF switch', next: 'power_level_switch_wiring', meta: { controlMethod: 'physical_switch' } },
        { value: 'other', label: 'Other / Not sure', next: 'RESULT', meta: { follow: FOLLOW.SWITCH_LOG, note: 'The power-level control method could not be identified automatically. SFG should review the setup before control-specific diagnostics are attempted.' } }
      ]
    },

    power_level_cruise_buttons: {
      id: 'power_level_cruise_buttons',
      type: 'choice',
      label: 'Are the cruise-control buttons functioning normally?',
      description: 'This application uses the cruise-control buttons to command power-level changes; it does not use a physical SOTF switch for level selection.',
      options: [
        { value: 'yes', label: 'Yes — cruise buttons work normally', next: 'power_level_behavior' },
        {
          value: 'no',
          label: 'No — cruise buttons are not working normally',
          stop: {
            title: 'Correct the cruise-control input issue first',
            text: 'Because the power-level system uses the cruise-control buttons, the button/input issue must be corrected before the SOTF/power-level function can be diagnosed.'
          }
        },
        {
          value: 'unsure',
          label: 'Not sure',
          stop: {
            title: 'Verify cruise-control button operation first',
            text: 'Confirm that the cruise-control buttons operate normally before continuing power-level diagnosis.'
          }
        }
      ]
    },

    power_level_switch_wiring: {
      id: 'power_level_switch_wiring',
      type: 'choice',
      label: 'Has the physical SOTF switch wiring, connector and firewall pass-through been inspected?',
      description: 'This question is only used on applications that actually use a physical SOTF switch. Switch failures are rare; wiring and connector issues are more common.',
      options: [
        { value: 'yes', label: 'Yes', next: 'power_level_behavior' },
        {
          value: 'no',
          label: 'No / Not sure',
          stop: {
            title: 'Inspect the switch wiring first',
            text: 'Inspect the physical SOTF switch wiring, connector and firewall pass-through before continuing. Do not use generic resistance/voltage specifications because switch arrangements differ by application.'
          }
        }
      ]
    },

    power_level_behavior: {
      id: 'power_level_behavior',
      type: 'choice',
      label: 'How does the concern behave across power levels?',
      options: [
        {
          value: 'all_same',
          label: 'All power levels behave the same / level does not appear to change',
          next: 'RESULT',
          meta: {
            status: STATUS.CONFIGURATION,
            follow: FOLLOW.SWITCH_LOG,
            flag: 'CALIBRATION CONFIGURATION CHECK — correct power-level-enabled file is reported loaded and the vehicle-specific control input has been checked, but commanded power levels do not appear to change.',
            evidence: { type: 'configuration', weight: 4, reason: 'Correct power-level-enabled file is reported loaded and the control input works, but all levels behave the same.' },
            note: 'The correct power-level-enabled file is reported loaded and the control input has been checked. SFG should verify the power-level/SOTF calibration configuration.'
          }
        },
        {
          value: 'high_power',
          label: 'Lower-power levels work, but higher-power levels do not make the expected power',
          next: 'power_level_high_power_dtcs',
          meta: {
            follow: FOLLOW.SWITCH_LOG,
            concern: 'running',
            evidence: { type: 'vehicle', weight: 1, reason: 'Concern appears only at higher requested power, which can expose an air/fuel/mechanical limitation but does not prove one.' }
          }
        },
        {
          value: 'one_two',
          label: 'Only one or two power levels behave unexpectedly',
          next: 'RESULT',
          meta: {
            status: STATUS.CONFIGURATION,
            follow: FOLLOW.SWITCH_LOG,
            evidence: { type: 'configuration', weight: 3, reason: 'Only specific commanded power levels behave unexpectedly while the power-level system otherwise functions.' },
            note: 'Only specific power levels behave unexpectedly. SFG should review the power-level calibration/configuration.'
          }
        },
        { value: 'other', label: 'Something else', next: 'RESULT', meta: { follow: FOLLOW.SWITCH_LOG } }
      ]
    },

    power_level_high_power_dtcs: {
      id: 'power_level_high_power_dtcs',
      type: 'dtc',
      label: 'Enter any current diagnostic trouble codes.',
      description: 'Because the concern appears only at higher power, current engine faults can identify relevant air, fuel, electrical or calibration-configuration checks. Enter NONE if the vehicle has been scanned and no codes are present.',
      placeholder: 'Example: P0087 P0299 — or NONE',
      requireScanned: true,
      next: 'power_level_dynamic'
    },

    power_level_dynamic: {
      id: 'power_level_dynamic',
      type: 'dynamic_vehicle_checks',
      meta: {
        follow: FOLLOW.SWITCH_LOG,
        note: 'The concern is limited to higher requested power. DTC-specific checks should be completed before deciding whether the limitation is vehicle-side or calibration-side.'
      }
    }

  };

  var FLOWS = {
    vehicle: {
      title: 'Vehicle Problem',
      start: 'vehicle_vin',
      technical: true,
      allowTicketWithoutVinOrOrder: false
    },
    flash: {
      title: 'Read / Flash Problem',
      start: 'flash_vin',
      technical: true,
      allowTicketWithoutVinOrOrder: false
    },
    device: {
      title: 'Device Connection',
      start: 'device_platform',
      technical: true,
      allowTicketWithoutVinOrOrder: false
    },
    tune: {
      title: 'Tune File Question',
      direct: true,
      technical: false,
      allowTicketWithoutVinOrOrder: true
    },
    licensing: {
      title: 'Licensing / Credits',
      start: 'licensing_platform',
      technical: false,
      allowTicketWithoutVinOrOrder: true
    },
    general: {
      title: 'General Support / Other',
      direct: true,
      technical: false,
      allowTicketWithoutVinOrOrder: true,
      status: STATUS.GENERAL
    }
  };

  function categories() {
    return [
      { key: 'vehicle', title: 'Vehicle Problem', description: 'Running, power, boost, no-start, transmission, SOTF/power-level, or fault code concerns.' },
      { key: 'flash', title: 'Read / Flash Problem', description: 'Problems reading, writing or recovering a controller.' },
      { key: 'device', title: 'Device Connection', description: 'Computer, phone or tuning device will not connect correctly.' },
      { key: 'tune', title: 'Tune File Question', description: 'Questions about a tune file, tune selection or a possible future order.' },
      { key: 'licensing', title: 'Licensing / Credits', description: 'Vehicle licenses, credits, activations or linking.' },
      { key: 'general', title: 'General Support / Other', description: 'Anything that does not fit the guided categories, including order or pre-sale questions.' }
    ];
  }

  function getFlow(key) {
    return FLOWS[key] || null;
  }

  function getNode(id) {
    return NODES[id] || null;
  }

  function getCheck(id) {
    return CHECKS[id] || null;
  }

  function routeNode(node, ctx) {
    if (!node) return null;
    if (node.type === 'router' && typeof node.route === 'function') {
      return node.route(ctx || {});
    }
    return node.next || null;
  }

  /*
    Vehicle base checks when no recognized DTC rule generates a more specific
    question set. This is intentionally short.
  */
  function fallbackVehicleChecks(ctx) {
    if (!ctx) return [];
    if (ctx.supportedApplication === false) return [];

    if (ctx.concern === 'no_start') {
      var noStart = ['full_flash', 'weak_crank', 'required_connectors', 'security_history', 'concern_timing'];
      if (supportsStockComparison(ctx.platform)) noStart.push('stock_compatibility');
      return noStart;
    }

    if (ctx.concern === 'transmission') {
      return ['transmission_built', 'concern_timing'];
    }

    if (ctx.concern === 'running') {
      var running = ['full_flash', 'required_connectors', 'charge_air_leaks', 'concern_timing'];
      if (supportsStockComparison(ctx.platform)) running.push('stock_compatibility');
      return running;
    }

    return ['full_flash', 'required_connectors', 'concern_timing'];
  }

  function platformFollow(platform, purpose) {
    if (purpose === 'power_level' || purpose === 'switch') return FOLLOW.SWITCH_LOG;
    if (platform === 'hp') return purpose === 'info' ? FOLLOW.HP_INFO_ONLY : FOLLOW.HP_INFO_THEN_DATALOG;
    if (platform === 'efi') return FOLLOW.EFI_LOG;
    if (platform === 'ez') return FOLLOW.EZ_REQUEST;
    return '';
  }

  window.SFGSupportFlow = {
    version: '2.12.0',
    urls: URLS,
    status: STATUS,
    follow: FOLLOW,
    flows: FLOWS,
    nodes: NODES,
    checks: CHECKS,
    categories: categories,
    getFlow: getFlow,
    getNode: getNode,
    getCheck: getCheck,
    routeNode: routeNode,
    deriveDtcPlan: deriveDtcPlan,
    customerDtcTitle: customerDtcTitle,
    customerDtcText: customerDtcText,
    deriveEfiPlan: deriveEfiPlan,
    fallbackVehicleChecks: fallbackVehicleChecks,
    platformFollow: platformFollow,
    isL5p: isL5p,
    isLb7Lly: isLb7Lly,
    isSupportedApplication: isSupportedApplication,
    powerLevelControlMethod: powerLevelControlMethod,
    supportsStockComparison: supportsStockComparison,
    resolveStatus: resolveStatus
  };
})();
